import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { HubShell, type HubTab } from '@/components/common/HubShell';
import { useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  APPOINTMENT_STATUS_OPTIONS,
  APPOINTMENT_STATUS_LABELS,
  calendarAccessSchema,
} from '@easyflow/shared';
import type {
  AppointmentStatus,
  CalendarBootstrapResponse,
  CalendarAccess,
  CalendarAppointment,
  CalendarSettingsRow,
  CalendarTimeSlot,
  CalendarServiceType,
  CalendarUser,
  CalendarUnifiedMonthCounts,
  CalendarInboxCount,
  CalendarGoogleSyncStatus,
  HrShiftGroup,
  HrAttendanceRecord,
} from '@easyflow/shared';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  User,
  Phone,
  Mail,
  MapPin,
  X,
  Edit,
  Trash2,
  Check,
  AlertCircle,
  Settings,
  Users,
  Loader2,
  RefreshCw,
  Pencil,
  CheckCircle,
  XCircle,
  Ban,
  ChevronDown,
  ChevronUp,
  Moon,
  Sun,
  Sunset,
  CloudMoon,
  Video,
  CheckSquare,
  ExternalLink,
  Copy,
  Lock,
  UserPlus,
  Building2,
  Sparkles,
  BarChart3,
  Inbox,
  BellRing,
  CalendarClock,
  ShoppingBag,
  MoreHorizontal,
  LayoutGrid,
 Filter as FilterIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { InteractionHistoryModal } from '@/components/InteractionHistoryModal';
import LeadTimelineModal from '@/components/crm/LeadTimelineModal';
import CalendarInboxTab from '@/components/calendar/CalendarInboxTab';
import CalendarAnalyticsTab from '@/components/calendar/CalendarAnalyticsTab';
import AvailabilityModeBadge from '@/components/calendar/AvailabilityModeBadge';
import BookingMatrixGrid from '@/components/calendar/BookingMatrixGrid';
import AppointmentPlanningView from '@/components/calendar/AppointmentPlanningView';
import AppointmentDetailPanel, { AppointmentLite } from '@/components/calendar/AppointmentDetailPanel';
import SkillMatrixSettings from '@/components/calendar/SkillMatrixSettings';
import CalendarSettingsPage from '@/pages/calendar/CalendarSettings';
import PlanningSkillDrawer from '@/components/calendar/PlanningSkillDrawer';
import AssigneeSkillsBadge from '@/components/calendar/AssigneeSkillsBadge';
import { useSocket } from '@/contexts/SocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Tabs, TabsContent } from '@easyflow/ui-frontend/components/ui/tabs';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@easyflow/ui-frontend/components/ui/tooltip';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@easyflow/ui-frontend/components/ui/collapsible';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@easyflow/ui-frontend/components/ui/sheet';
import AppointmentSourceBadge from '@/components/calendar/AppointmentSourceBadge';
import {
  APPOINTMENT_SOURCE_TAXONOMY,
  type AppointmentSourceKind,
} from '@/lib/appointment-source-taxonomy';
import CockpitNowNextCards from '@/components/calendar/CockpitNowNextCards';
import CockpitDailyStats from '@/components/calendar/CockpitDailyStats';
import CockpitEventList, { CockpitAllDayEvents } from '@/components/calendar/CockpitEventList';
import CockpitAdminMatrix from '@/components/calendar/CockpitAdminMatrix';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { getTenantNavigation } from '@/lib/navigation';
import { canWriteCalendarObject } from '@/lib/calendar-access';
import { useModules } from '@/contexts/ModuleContext';
import { useToast } from '@/hooks/use-toast';
import { cn, getErrorMessage } from '@/lib/utils';
import { shouldShowProductPicker, autoSelectSingleProduct } from '@/lib/appointment-service-selection';
import { useIsMobile } from '@/hooks/use-media-query';
import type { UnifiedEvent } from '@/components/calendar/types';

function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Task #3694: la riga appuntamento (con alias joinati) è ora il DTO condiviso
// `CalendarAppointment` importato da '@easyflow/shared'. `metadata` è
// `unknown` sul contratto: dove leggiamo campi specifici usiamo un narrowing
// tipato locale (vedi `appointmentMetadata`).

/**
 * Task #3694: accessor tipato per i campi di `metadata` (jsonb `unknown` nel
 * DTO shared) letti in questa pagina. Nessun cambiamento runtime: legge le
 * stesse proprietà via una vista tipata sull'oggetto (fallback a `{}`).
 */
interface AppointmentMetadataView {
  product_name?: string;
  product_price?: string | number | null;
  product_currency?: string;
  source?: string;
}
const appointmentMetadata = (a: { metadata?: unknown } | null | undefined): AppointmentMetadataView =>
  (a?.metadata && typeof a.metadata === 'object' ? (a.metadata as AppointmentMetadataView) : {});

interface ContactSearchResult {
  id: string;
  contact_type: 'customer' | 'lead';
  first_name?: string;
  last_name?: string;
  company_name?: string;
  customer_type?: string;
  email?: string;
  phone?: string;
}

// Task #3694: ServiceType, TimeSlot, UnifiedMonthCounts, CalendarSettings e
// CalendarUser sono ora i DTO condivisi CalendarServiceType, CalendarTimeSlot,
// CalendarUnifiedMonthCounts, CalendarSettingsRow e CalendarUser importati da
// '@easyflow/shared'.

const DAYS_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const MONTHS_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Task #526: palette estesa coerente per i 7 stati operativi
const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  scheduled:   { bg: 'bg-blue-100 dark:bg-blue-900/30',     text: 'text-blue-700 dark:text-blue-300',     border: 'border-blue-300 dark:border-blue-700' },
  confirmed:   { bg: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-700 dark:text-green-300',   border: 'border-green-300 dark:border-green-700' },
  reminded:    { bg: 'bg-amber-100 dark:bg-amber-900/30',   text: 'text-amber-700 dark:text-amber-300',   border: 'border-amber-300 dark:border-amber-700' },
  rescheduled: { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-700 dark:text-violet-300', border: 'border-violet-300 dark:border-violet-700' },
  cancelled:   { bg: 'bg-red-100 dark:bg-red-900/30',       text: 'text-red-700 dark:text-red-300',       border: 'border-red-300 dark:border-red-700' },
  completed:   { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-300 dark:border-emerald-700' },
  no_show:     { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-700' },
};

const STATUS_LABELS: Record<string, string> = APPOINTMENT_STATUS_LABELS;

const STATUS_ICONS: Record<string, LucideIcon> = {
  scheduled: Clock,
  confirmed: CheckCircle,
  reminded: BellRing,
  rescheduled: CalendarClock,
  cancelled: XCircle,
  completed: Check,
  no_show: Ban,
};

const CALENDAR_MAIN_TABS: readonly HubTab<string>[] = [
  { key: 'calendar',     label: 'Calendario',        mobileLabel: 'Calendario', icon: CalendarIcon },
  { key: 'planning',     label: 'Planning',          mobileLabel: 'Planning',   icon: CalendarClock },
  { key: 'matrix',       label: 'Matrice',           mobileLabel: 'Matrice',    icon: Users },
  { key: 'skill-matrix', label: 'Matrice Competenze', mobileLabel: 'Competenze', icon: Users },
  { key: 'analytics',    label: 'Analytics',         mobileLabel: 'Analytics',  icon: BarChart3 },
  { key: 'inbox',        label: 'Inbox',             mobileLabel: 'Inbox',      icon: Inbox },
  { key: 'settings',     label: 'Impostazioni',      mobileLabel: 'Impostazioni', icon: Settings },
];

export default function CalendarPage() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [access, setAccess] = useState<CalendarAccess | null>(null);
  const canManage = hasPermission('calendar.manage') && access?.canManage === true;
  const canViewCrm = hasPermission('crm.view');
  const canEditAppointment = (appointment: { user_id?: string | null; assigned_to_id?: string | null }) =>
    canManage && canWriteCalendarObject(access, appointment, 'edit');
  const canDeleteAppointment = (appointment: { user_id?: string | null; assigned_to_id?: string | null }) =>
    canManage && canWriteCalendarObject(access, appointment, 'delete');
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const tenantNavigation = getTenantNavigation(tenantSlug);
  const calendarPath = tenantNavigation.find(item => item.permission === 'calendar.view')?.href;
  const tasksPath = tenantNavigation.find(item => item.permission === 'tasks.view')?.href;
  const workgroupsPath = tenantNavigation.flatMap(item => item.children ?? [])
    .find(item => item.href.endsWith('/messenger/gruppi'))?.href.replace(/\/gruppi$/, '/workgroups');
  const { isModuleEnabled } = useModules();
  const { toast } = useToast();
  const { on, off, socketVersion } = useSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // Task #465 / #553: 5 tab top-level (calendar, matrix, skill-matrix, analytics, inbox).
  // Le vecchie route ?tab=forms|availability|reassignments|skill-overrides|automations|types
  // ora ridirigono alla pagina dedicata /calendar/settings sulla sezione corretta.
  const TOP_LEVEL_TABS = ['calendar', 'planning', 'matrix', 'skill-matrix', 'analytics', 'inbox', 'settings'];
  const SETTINGS_TAB_MAP: Record<string, string> = useMemo(() => ({
    forms: 'forms',
    availability: 'availability',
    reassignments: 'reassignments',
    'skill-overrides': 'skill-overrides',
    'override-audit': 'skill-overrides',
    automations: 'automations',
    types: 'types',
  }), []);
  // Task #545: default tab è `planning` per ruoli admin/super_admin/manager,
  // `calendar` per tutti gli altri. L'URL `?tab=` continua a sovrascrivere
  // (es. `?tab=matrix` per aprire direttamente la Matrice). Il default viene
  // anche riallineato in un effect quando il ruolo viene risolto in modo
  // asincrono (l'auth context può idratare dopo il primo render): in tal
  // caso applichiamo il default solo se l'utente non ha mai cambiato tab e
  // l'URL non specifica un tab esplicito.
  const [activeTab, setActiveTab] = useState<string>(() => {
    const t = searchParams.get('tab');
    if (t && TOP_LEVEL_TABS.includes(t)) return t;
    return 'calendar';
  });
  const tabUserOverrideRef = useRef(false);

  const handleTabChange = useCallback((val: string) => {
    tabUserOverrideRef.current = true;
    setActiveTab(val);
    const params = new URLSearchParams(searchParams);
    if (val === 'calendar') params.delete('tab');
    else params.set('tab', val);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Default tab = "Calendario" per TUTTI gli utenti (admin inclusi).
  // L'utente ha richiesto esplicitamente che entrando in /calendar la prima
  // tab visibile sia "Calendario", non "Planning".

  // On mobile the day calendar grid is collapsed by default; the day-list
  // is the primary surface. The effect re-asserts the collapse on viewport
  // changes so resize/orientation always lands on the list view.
  const [mobileCalendarExpanded, setMobileCalendarExpanded] = useState(false);
  useEffect(() => {
    if (isMobile) setMobileCalendarExpanded(false);
  }, [isMobile]);
  
  const [appointments, setAppointments] = useState<CalendarAppointment[]>([]);
  const [dayAppointments, setDayAppointments] = useState<CalendarAppointment[]>([]);
  const [timeSlots, setTimeSlots] = useState<CalendarTimeSlot[]>([]);
  // Task #526: drawer Planning per dettagli risorsa (skill / turni / servizi)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerResource, setDrawerResource] = useState<{ id: string; name: string } | null>(null);
  // Task #545: side/bottom-sheet dettaglio appuntamento per il nuovo tab Planning
  const [planningDetailId, setPlanningDetailId] = useState<string | null>(null);
  const [planningDetailInitial, setPlanningDetailInitial] = useState<AppointmentLite | null>(null);
  const [planningDetailOpen, setPlanningDetailOpen] = useState(false);
  const [monthCounts, setMonthCounts] = useState<Record<string, number>>({});

  const [unifiedEvents, setUnifiedEvents] = useState<UnifiedEvent[]>([]);
  const [unifiedMonthCounts, setUnifiedMonthCounts] = useState<CalendarUnifiedMonthCounts>({});
  const [sourcesFilter, setSourcesFilter] = useState<{ appointments: boolean; tasks: boolean; videocalls: boolean; workgroup_events: boolean }>({
    appointments: true,
    tasks: true,
    videocalls: true,
    workgroup_events: true,
  });
  const [selectedTaskEvent, setSelectedTaskEvent] = useState<UnifiedEvent | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedVideoEvent, setSelectedVideoEvent] = useState<UnifiedEvent | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedWorkgroupEvent, setSelectedWorkgroupEvent] = useState<UnifiedEvent | null>(null);
  const [showWorkgroupEventModal, setShowWorkgroupEventModal] = useState(false);
  const [serviceTypes, setServiceTypes] = useState<CalendarServiceType[]>([]);
  // Task #649: forms attivi (per la sezione "Form di prenotazione")
  // e prodotti collegati al tipo selezionato (per la sezione "Prodotto/Servizio").
  const [appointmentForms, setAppointmentForms] = useState<Array<{
    id: string;
    name: string;
    slug: string;
    is_active: boolean;
    custom_fields: Array<{ id: string; label: string; type: string; required?: boolean; options?: string[]; placeholder?: string }>;
  }>>([]);
  const [linkedProducts, setLinkedProducts] = useState<Array<{
    id: string;
    name: string;
    description: string | null;
    price: number | null;
  }>>([]);
  const [settings, setSettings] = useState<CalendarSettingsRow | null>(null);
  const [calendarUsers, setCalendarUsers] = useState<CalendarUser[]>([]);
  
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [selectedAppointment, setSelectedAppointment] = useState<CalendarAppointment | null>(null);
  const [customerCardTarget, setCustomerCardTarget] = useState<{ type: 'b2c' | 'b2b' | 'person'; id: string; name: string } | null>(null);
  const [calendarLeadTimeline, setCalendarLeadTimeline] = useState<{ id: string; name: string } | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [newAppointmentStep, setNewAppointmentStep] = useState(1);
  const [bestResourceSuggestion, setBestResourceSuggestion] = useState<{
    user_id: string;
    full_name: string;
    score: number;
    reasons: string[];
  } | null>(null);
  const [bestResourceLoading, setBestResourceLoading] = useState(false);
  const [bestResourceNoCandidates, setBestResourceNoCandidates] = useState(false);
  // Task #3040 — modalità del modale "Nuovo Appuntamento":
  // 'rapido'/'completo' = appuntamento (campi essenziali / avanzati);
  // 'personale' = evento privato dell'utente (tabella dedicata, no reminder).
  const [naMode, setNaMode] = useState<'rapido' | 'completo' | 'personale'>('rapido');
  const [naMoreOpen, setNaMoreOpen] = useState(false);
  // Task #3040 — stato dei campi dell'evento personale.
  const [naPersonal, setNaPersonal] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    all_day: false,
    color: '#0ea5e9',
  });
  const [savingPersonalEvent, setSavingPersonalEvent] = useState(false);
  // Task #3040 — filtri rosa "Assegnato a" nella sezione Completo (mixabili):
  // competenze (skill richieste soddisfatte) e turni (in turno sullo slot).
  const [naFilterSkills, setNaFilterSkills] = useState(false);
  const [naFilterShifts, setNaFilterShifts] = useState(false);
  const [naEligibility, setNaEligibility] = useState<Record<string, { skillsOk: boolean; shiftOk: boolean }>>({});
  const [naEligibilityLoading, setNaEligibilityLoading] = useState(false);
  const [naTitleDirty, setNaTitleDirty] = useState(false);
  const [naAssigneeDirty, setNaAssigneeDirty] = useState(false);
  const [slotConflict, setSlotConflict] = useState<{
    occupiedBy: string;
    nextFree: { start: string; end: string } | null;
  } | null>(null);
  const [showEditAppointmentModal, setShowEditAppointmentModal] = useState(false);
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);
  const [inboxCount, setInboxCount] = useState<number>(0);

  const [loading, setLoading] = useState(true);
  const [savingAppointment, setSavingAppointment] = useState(false);
  
  const [newAppointment, setNewAppointment] = useState({
    title: '',
    description: '',
    service_type_id: '',
    start_time: '',
    end_time: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_id: '',
    lead_id: '',
    assigned_to_id: '',
    observer_ids: [] as string[],
    campaign_id: '',
    status: 'scheduled' as AppointmentStatus,
    location: '',
    notes: '',
    internal_notes: '',
    store_id: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    reminder_offsets: [] as number[],
    // Task #649: campi aggiuntivi esposti dalla "Più opzioni"
    source_kind: 'manuale_admin' as AppointmentSourceKind,
    appointment_form_id: '' as string,
    form_values: {} as Record<string, string | boolean>,
    product_id: '' as string,
    product_name: '' as string,
    product_price: null as number | null,
    product_quantity: 1 as number,
    product_note: '' as string,
    scheduled_reminders: [] as Array<{ channel: 'email' | 'sms' | 'whatsapp'; offset_minutes: number }>,
  });
  
  const [includeVideoMeeting, setIncludeVideoMeeting] = useState(false);

  const [skillOverrideDialog, setSkillOverrideDialog] = useState<{
    open: boolean;
    mode: 'create' | 'update';
    missingSkills: string[];
    canOverride: boolean;
    message: string;
  }>({ open: false, mode: 'create', missingSkills: [], canOverride: false, message: '' });
  const [skillOverrideReason, setSkillOverrideReason] = useState('');
  const [submittingOverride, setSubmittingOverride] = useState(false);
  const [stores, setStores] = useState<Array<{id: string; name: string; city?: string; address?: string}>>([]);
  const [campaigns, setCampaigns] = useState<Array<{id: string; name: string}>>([]);
  const [prefillCampaignId, setPrefillCampaignId] = useState<string>('');
  
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<ContactSearchResult[]>([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  
  const [googleSyncStatus, setGoogleSyncStatus] = useState<CalendarGoogleSyncStatus | null>(null);
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [togglingGoogleSync, setTogglingGoogleSync] = useState(false);
  const [selectedSyncAccountId, setSelectedSyncAccountId] = useState<string>('');
  const [googleAccountFilter, setGoogleAccountFilter] = useState<string>('all');

  const [dayShifts, setDayShifts] = useState<HrShiftGroup[]>([]);
  const [monthShifts, setMonthShifts] = useState<Record<string, HrShiftGroup[]>>({});
  const [shiftsExpanded, setShiftsExpanded] = useState(true);
  const [dayAttendance, setDayAttendance] = useState<HrAttendanceRecord[]>([]);

  // Task #524 / #553: editor multi-regola spostato in `ServiceTypesSettings`
  // dentro la pagina dedicata /calendar/settings.

  const isBroad = access?.isBroad === true;
  const isAdmin = access?.isAdmin === true;

  // Task #595: filtro multi-select per la nuova tassonomia sorgenti unificata.
  // Sostituisce il vecchio originFilter ('all'|'public_form'|'manual'), che era
  // limitato a 2 categorie. Vuoto = nessun filtro (tutte le sorgenti).
  const [sourceKindsFilter, setSourceKindsFilter] = useState<AppointmentSourceKind[]>([]);

  const filteredDayAppointments = useMemo(() => {
    let list = dayAppointments;
    if (googleAccountFilter !== 'all') {
      list = list.filter(apt => apt.google_account_email === googleAccountFilter);
    }
    // Task #595: filtraggio per tassonomia sorgente unificata.
    if (sourceKindsFilter.length > 0) {
      list = list.filter(apt => {
        // source_kind è string|null sul DTO shared: cast alla tassonomia nota
        // per il match (nessun cambiamento runtime).
        const k = apt.source_kind as AppointmentSourceKind | null;
        return k ? sourceKindsFilter.includes(k) : false;
      });
    }
    return list;
  }, [dayAppointments, googleAccountFilter, sourceKindsFilter]);

  // Task #595: applica gli stessi filtri (account Google + tassonomia
  // sorgente) alla lista unificata di eventi, in modo che la UI principale
  // (AllDayEvents, TimedEvents, contatori header, drawer giornaliero) e
  // le schede ne siano coerenti col multi-select sorgente. Per gli eventi
  // non-appuntamento (task, videocall, workgroup) il filtro sorgente non
  // si applica: vengono nascosti solo se sourceKindsFilter è non vuoto
  // (perché in quel caso l'utente sta esplicitamente richiedendo
  // "appuntamenti di queste sorgenti").
  const allowedAppointmentIds = useMemo(
    () => new Set(filteredDayAppointments.map(a => a.id)),
    [filteredDayAppointments]
  );
  const filteredUnifiedEvents = useMemo(() => {
    if (googleAccountFilter === 'all' && sourceKindsFilter.length === 0) {
      return unifiedEvents;
    }
    return unifiedEvents.filter(ev => {
      if (ev.source !== 'appointment') {
        return sourceKindsFilter.length === 0;
      }
      const apptId = ev.metadata?.appointmentId;
      return apptId ? allowedAppointmentIds.has(apptId) : false;
    });
  }, [unifiedEvents, allowedAppointmentIds, googleAccountFilter, sourceKindsFilter]);

  // Service-type view models retain their optional presentation fields.
  // Settings are consumed directly from the shared nullable DTO.
  const planningServiceTypes = useMemo(
    () => serviceTypes.map(t => ({
      ...t,
      color: t.color ?? undefined,
      is_active: t.is_active ?? undefined,
    })),
    [serviceTypes]
  );

  const toLocalDatetimeString = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const loadGoogleSyncStatus = async () => {
    try {
      const res = await apiClient.get<CalendarGoogleSyncStatus>('/calendar/google-sync/status');
      if (res.success && res.data) {
        setGoogleSyncStatus(res.data);
      }
    } catch (e) {
      console.error('Error loading Google sync status:', e);
    }
  };

  // Task #2612: il caricamento iniziale legge i query param via ref (non via
  // dipendenza) così la normalizzazione URL (es. scrittura di ?storeId=…) NON
  // ricrea il callback né riavvia il fetch → niente flicker doppio-caricamento.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  // Lo spinner full-page appare SOLO al primissimo caricamento; i refetch in
  // background non rialzano lo stato `loading` globale.
  const initialLoadDoneRef = useRef(false);
  // Task #2656 — il bootstrap consolidato carica la vista-giorno critica in una
  // sola richiesta; gli effetti secondari (mese, unified, turni, presenze,
  // socket) restano gated finché il bootstrap non ha dipinto il primo frame.
  const [bootstrapReady, setBootstrapReady] = useState(false);
  // Il bootstrap include già appuntamenti+slot del giorno iniziale: salta il
  // primo loadDayData ridondante scatenato dall'effetto-giorno post-bootstrap.
  const skipNextDayLoadRef = useRef(false);

  const loadBootstrap = useCallback(async () => {
    if (!initialLoadDoneRef.current) setLoading(true);
    try {
      const dateStr = toLocalDateStr(selectedDate);
      const params = new URLSearchParams({ date: dateStr });
      const urlStoreId = searchParamsRef.current.get('storeId') || '';
      if (urlStoreId) params.set('storeId', urlStoreId);
      const userParam = selectedUserId && selectedUserId !== 'all' ? selectedUserId : '';
      if (userParam) params.set('user', userParam);

      const res = await apiClient.get<CalendarBootstrapResponse<
        CalendarSettingsRow,
        CalendarServiceType,
        CalendarUser,
        { id: string; name: string; city?: string; address?: string },
        { id: string; name: string; slug: string; is_active: boolean; custom_fields: Array<{ id: string; label: string; type: string; required?: boolean; options?: string[]; placeholder?: string }> },
        { id: string; name: string },
        CalendarAppointment,
        CalendarTimeSlot
      >>(`/calendar/bootstrap?${params.toString()}`);

      if (!res.success || !res.data) {
        throw new Error('Bootstrap calendario non riuscito');
      }
      const d = res.data;
      const effectiveAccess = calendarAccessSchema.parse(d.access);
      setAccess(effectiveAccess);

      setServiceTypes(d.types || []);
      setAppointmentForms((d.forms || []).filter(f => f && f.is_active !== false));
      setCampaigns(Array.isArray(d.campaigns) ? d.campaigns : []);

      const storesList = Array.isArray(d.stores) ? d.stores : [];
      setStores(storesList);
      // La sede selezionata è risolta lato server (param URL valido → corrente →
      // prima alfabetica). La adottiamo direttamente.
      if (d.selectedStoreId) {
        setSelectedStoreId(prev => prev || d.selectedStoreId || '');
      }

      if (d.settings) {
        setSettings(d.settings);
      }

      setCalendarUsers(d.users || []);
      if (user && !effectiveAccess.isBroad) {
        setSelectedUserId(user.id);
      }

      // Vista-giorno critica già pronta nel bootstrap.
      setDayAppointments(d.appointments || []);
      setTimeSlots(d.slots || []);
      skipNextDayLoadRef.current = true;

      if (effectiveAccess.canManage) {
        loadGoogleSyncStatus();
      }
    } catch (error) {
      setAccess(null);
      console.error('Error loading calendar bootstrap:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile caricare i dati del calendario',
        variant: 'destructive'
      });
    } finally {
      initialLoadDoneRef.current = true;
      setLoading(false);
      setBootstrapReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  }, [toast, user]);

  useEffect(() => {
    loadBootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  }, []);

  // Task #465 / #553: URL back-compat — vecchi tab (forms, availability, ecc.)
  // sono diventati sezioni del nuovo tab "Impostazioni" embeddato.
  // Normalizziamo ?tab=forms → ?tab=settings&section=forms restando in /calendar.
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && SETTINGS_TAB_MAP[t]) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'settings');
      next.set('section', SETTINGS_TAB_MAP[t]);
      setSearchParams(next, { replace: true });
      setActiveTab('settings');
      tabUserOverrideRef.current = true;
    }
  }, [SETTINGS_TAB_MAP, searchParams, setSearchParams]);

  // Task #560: sincronizzazione bidirezionale URL ↔ selectedStoreId.
  // Permette bookmark/back-button con la sede corretta. La selezione iniziale
  // viene scelta da loadInitialData (URL > prev > prima alfabetica); qui
  // teniamo l'URL allineato a ogni cambio di sede dell'utente.
  useEffect(() => {
    const current = searchParams.get('storeId') || '';
    if (selectedStoreId && current !== selectedStoreId) {
      const next = new URLSearchParams(searchParams);
      next.set('storeId', selectedStoreId);
      setSearchParams(next, { replace: true });
    } else if (!selectedStoreId && current) {
      const next = new URLSearchParams(searchParams);
      next.delete('storeId');
      setSearchParams(next, { replace: true });
    }
  }, [selectedStoreId, searchParams, setSearchParams]);

  // Task #465: badge counter Inbox — refresh ad ogni evento socket + polling 30s.
  const loadInboxCount = useCallback(async () => {
    try {
      const res = await apiClient.get<CalendarInboxCount>('/calendar/inbox/count');
      if (res?.success) setInboxCount(res.data?.count || 0);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadInboxCount();
    const id = setInterval(loadInboxCount, 30000);
    const handler = () => loadInboxCount();
    on?.('calendar:appointment_changed', handler);
    return () => {
      clearInterval(id);
      off?.('calendar:appointment_changed', handler);
    };
  }, [loadInboxCount, on, off, socketVersion]);

  useEffect(() => {
    const state = location.state as { prefillAppointment?: {
      contact_name?: string;
      contact_phone?: string;
      contact_email?: string;
      lead_id?: string;
      campaign_id?: string;
      notes?: string;
    }} | null;
    
    if (state?.prefillAppointment && bootstrapReady) {
      if (!canManage) {
        toast({ title: 'Sola lettura', description: 'Per creare appuntamenti o eventi personali è richiesto calendar.manage.', variant: 'destructive' });
        return;
      }
      const prefill = state.prefillAppointment;
      const now = new Date();
      now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0);
      const endTime = new Date(now.getTime() + 30 * 60000);
      
      if (prefill.campaign_id) {
        setPrefillCampaignId(prefill.campaign_id);
      }
      setNewAppointment(prev => ({
        ...prev,
        title: prefill.contact_name ? `Appuntamento con ${prefill.contact_name}` : '',
        customer_name: prefill.contact_name || '',
        customer_phone: prefill.contact_phone || '',
        customer_email: prefill.contact_email || '',
        lead_id: prefill.lead_id || '',
        campaign_id: prefill.campaign_id || '',
        notes: '',
        start_time: toLocalDatetimeString(now),
        end_time: toLocalDatetimeString(endTime),
        assigned_to_id: user?.id || ''
      }));
      setSelectedDate(now);
      setNewAppointmentStep(1);
      setShowNewAppointmentModal(true);
      
      window.history.replaceState({}, document.title);
    }
  }, [location.state, user?.id, bootstrapReady, canManage, toast]);

  const appointmentIdParam = searchParams.get('appointmentId');
  useEffect(() => {
    const appointmentId = appointmentIdParam;
    if (!appointmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<CalendarAppointment>(`/calendar/appointments/${appointmentId}`);
        if (cancelled) return;
        const apt = res.data;
        if (!apt || !apt.id) return;
        const aptDate = new Date(apt.start_time);
        setSelectedDate(aptDate);
        setCurrentDate(aptDate);
        setActiveTab('calendar');
        setSelectedAppointment(apt);
        setShowAppointmentModal(true);
      } catch (err) {
        toast({ title: 'Appuntamento non trovato', description: 'Impossibile aprire l\'appuntamento richiesto', variant: 'destructive' });
      } finally {
        if (!cancelled) {
          const params = new URLSearchParams(searchParams);
          params.delete('appointmentId');
          setSearchParams(params, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [appointmentIdParam, searchParams, setSearchParams, toast]);

  const getUserFilterParam = useCallback((paramName = 'user') => {
    if (isBroad) {
      if (selectedUserId && selectedUserId !== 'all') return `${paramName}=${selectedUserId}`;
      return '';
    }
    return user?.id ? `${paramName}=${user.id}` : '';
  }, [isBroad, selectedUserId, user?.id]);

  const loadMonthData = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const queryParts: string[] = [];
      const uf = getUserFilterParam('user');
      if (uf) queryParts.push(uf);
      if (selectedStoreId) queryParts.push(`store=${selectedStoreId}`);
      const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
      
      const response = await apiClient.get<Record<string, number>>(`/calendar/appointments/month/${year}/${month}${params}`);
      if (response.success) {
        setMonthCounts(response.data || {});
      }
    } catch (error) {
      console.error('Error loading month data:', error);
    }
  }, [currentDate, getUserFilterParam, selectedStoreId]);

  const getSourcesParam = useCallback(() => {
    const active: string[] = [];
    if (sourcesFilter.appointments) active.push('appointments');
    if (sourcesFilter.tasks) active.push('tasks');
    if (sourcesFilter.videocalls) active.push('videocalls');
    if (sourcesFilter.workgroup_events) active.push('workgroup_events');
    return active.join(',');
  }, [sourcesFilter.appointments, sourcesFilter.tasks, sourcesFilter.videocalls, sourcesFilter.workgroup_events]);

  const loadUnifiedMonthData = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const queryParts: string[] = [];
      queryParts.push(`sources=${getSourcesParam()}`);
      const uf = getUserFilterParam('userId');
      if (uf) queryParts.push(uf);
      if (selectedStoreId) queryParts.push(`storeId=${selectedStoreId}`);
      const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

      const response = await apiClient.get<CalendarUnifiedMonthCounts>(`/calendar/unified/month/${year}/${month}${params}`);
      if (response.success) {
        setUnifiedMonthCounts(response.data || {});
      }
    } catch (error) {
      console.error('Error loading unified month data:', error);
    }
  }, [currentDate, getSourcesParam, getUserFilterParam, selectedStoreId]);

  const loadMonthShifts = useCallback(async () => {
    if (!isModuleEnabled('hr')) return;
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const lastDay = new Date(year, month, 0).getDate();
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const dateTo = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const queryParts: string[] = [`date_from=${dateFrom}`, `date_to=${dateTo}`];
      if (selectedStoreId) queryParts.push(`store_id=${selectedStoreId}`);
      const response = await apiClient.get<Record<string, HrShiftGroup[]>>(`/hr/shifts/by-date?${queryParts.join('&')}`);
      if (response.success && response.data) {
        setMonthShifts(response.data);
      } else {
        setMonthShifts({});
      }
    } catch {
      setMonthShifts({});
    }
  }, [currentDate, isModuleEnabled, selectedStoreId]);

  useEffect(() => {
    // Task #2656 — i contatori mese/unified/turni sono NON critici: differiti
    // finché il bootstrap non ha dipinto la vista-giorno.
    if (!bootstrapReady) return;
    loadMonthData();
    loadUnifiedMonthData();
    loadMonthShifts();
  }, [bootstrapReady, currentDate, selectedUserId, selectedStoreId, sourcesFilter, loadMonthData, loadMonthShifts, loadUnifiedMonthData]);

  const loadDayData = useCallback(async () => {
    try {
      const dateStr = toLocalDateStr(selectedDate);
      const queryParts: string[] = [];
      const uf = getUserFilterParam('user');
      if (uf) queryParts.push(uf);
      if (selectedStoreId) queryParts.push(`store=${selectedStoreId}`);
      const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
      
      const [appointmentsRes, slotsRes] = await Promise.all([
        apiClient.get<CalendarAppointment[]>(`/calendar/appointments/date/${dateStr}${params}`),
        apiClient.get<CalendarTimeSlot[]>(`/calendar/slots/${dateStr}${params}`)
      ]);

      if (appointmentsRes.success) {
        setDayAppointments(appointmentsRes.data || []);
      }
      
      if (slotsRes.success) {
        setTimeSlots(slotsRes.data || []);
      }
    } catch (error) {
      console.error('Error loading day data:', error);
    }
  }, [getUserFilterParam, selectedDate, selectedStoreId]);

  const loadUnifiedDayData = useCallback(async () => {
    try {
      const dateStr = toLocalDateStr(selectedDate);
      const queryParts: string[] = [];
      queryParts.push(`sources=${getSourcesParam()}`);
      const uf = getUserFilterParam('userId');
      if (uf) queryParts.push(uf);
      if (selectedStoreId) queryParts.push(`storeId=${selectedStoreId}`);
      const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

      const response = await apiClient.get<UnifiedEvent[]>(`/calendar/unified/${dateStr}${params}`);
      if (response.success) {
        setUnifiedEvents(response.data || []);
      }
    } catch (error) {
      console.error('Error loading unified day data:', error);
    }
  }, [getSourcesParam, getUserFilterParam, selectedDate, selectedStoreId]);

  const loadDayShifts = useCallback(async () => {
    if (!isModuleEnabled('hr')) return;
    try {
      const dateStr = toLocalDateStr(selectedDate);
      const queryParts: string[] = [`date=${dateStr}`];
      if (selectedStoreId) queryParts.push(`store_id=${selectedStoreId}`);
      const response = await apiClient.get<Record<string, HrShiftGroup[]>>(`/hr/shifts/by-date?${queryParts.join('&')}`);
      if (response.success && response.data) {
        setDayShifts(response.data[dateStr] || []);
      } else {
        setDayShifts([]);
      }
    } catch {
      setDayShifts([]);
    }
  }, [isModuleEnabled, selectedDate, selectedStoreId]);

  const loadDayAttendance = useCallback(async () => {
    if (!isModuleEnabled('hr')) return;
    try {
      const dateStr = toLocalDateStr(selectedDate);
      const queryParts: string[] = [`date_from=${dateStr}`, `date_to=${dateStr}`];
      if (selectedStoreId) queryParts.push(`store_id=${selectedStoreId}`);
      const response = await apiClient.get<HrAttendanceRecord[]>(`/hr/attendance/records?${queryParts.join('&')}`);
      if (response.success && response.data) {
        const uniqueByUser = new Map<string, HrAttendanceRecord>();
        for (const r of response.data) {
          if (!uniqueByUser.has(r.user_id) || new Date(r.clock_in) > new Date((uniqueByUser.get(r.user_id) ?? r).clock_in)) {
            uniqueByUser.set(r.user_id, r);
          }
        }
        setDayAttendance(Array.from(uniqueByUser.values()));
      } else {
        setDayAttendance([]);
      }
    } catch {
      setDayAttendance([]);
    }
  }, [isModuleEnabled, selectedDate, selectedStoreId]);

  useEffect(() => {
    // Task #2656 — gated dietro il bootstrap. Il primo passaggio post-bootstrap
    // salta loadDayData (appuntamenti+slot già forniti dal bootstrap) ma carica
    // comunque le sezioni NON incluse (unified/turni/presenze).
    if (!bootstrapReady) return;
    if (skipNextDayLoadRef.current) {
      skipNextDayLoadRef.current = false;
    } else {
      loadDayData();
    }
    loadUnifiedDayData();
    loadDayShifts();
    loadDayAttendance();
  }, [bootstrapReady, selectedDate, selectedUserId, selectedStoreId, sourcesFilter, loadDayAttendance, loadDayData, loadDayShifts, loadUnifiedDayData]);

  useEffect(() => {
    const handler = () => {
      loadDayData();
      loadUnifiedDayData();
      loadMonthData();
      loadUnifiedMonthData();
    };
    on('calendar:appointment_changed', handler);
    return () => { off('calendar:appointment_changed', handler); };
  }, [on, off, socketVersion, selectedDate, currentDate, selectedUserId, selectedStoreId, sourcesFilter, loadDayData, loadMonthData, loadUnifiedDayData, loadUnifiedMonthData]);

  // Best-resource suggestion for the "Nuovo Appuntamento" wizard step 3.
  // Calls the resolver per-slot endpoint with the single chosen slot,
  // debounced 250ms (same as AssigneeSkillsBadge) to avoid hammering on
  // every keystroke when the user edits the datetime fields.
  useEffect(() => {
    if (!showNewAppointmentModal) {
      setBestResourceSuggestion(null);
      setBestResourceLoading(false);
      setBestResourceNoCandidates(false);
      return;
    }
    const serviceId = newAppointment.service_type_id;
    const startStr = newAppointment.start_time;
    const endStr = newAppointment.end_time;
    if (!serviceId || !startStr || !endStr) {
      setBestResourceSuggestion(null);
      setBestResourceLoading(false);
      setBestResourceNoCandidates(false);
      return;
    }
    const sd = new Date(startStr);
    const ed = new Date(endStr);
    if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime()) || ed <= sd) {
      setBestResourceSuggestion(null);
      setBestResourceLoading(false);
      setBestResourceNoCandidates(false);
      return;
    }
    let cancelled = false;
    setBestResourceLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await apiClient.post<{
          slot_rankings: Array<{
            start: string;
            end: string;
            candidates: Array<{
              user_id: string;
              eligible?: boolean;
              score?: number;
              reasons?: string[];
              full_name?: string;
              first_name?: string | null;
              last_name?: string | null;
            }>;
          }>;
        }>('/calendar/eligible-resources/per-slot', {
          service_id: serviceId,
          slots: [{ start: sd.toISOString(), end: ed.toISOString() }],
          store_id: newAppointment.store_id || null,
        });
        if (cancelled) return;
        const ranking = res.success && res.data ? res.data.slot_rankings?.[0] : null;
        const top = ranking?.candidates?.find(c => c.eligible) || null;
        if (!top) {
          setBestResourceSuggestion(null);
          setBestResourceNoCandidates(true);
        } else {
          const fallbackName = calendarUsers.find(u => u.id === top.user_id)?.name;
          setBestResourceSuggestion({
            user_id: top.user_id,
            full_name: top.full_name
              || [top.first_name, top.last_name].filter(Boolean).join(' ').trim()
              || fallbackName
              || 'Risorsa',
            score: top.score ?? 0,
            reasons: top.reasons || [],
          });
          setBestResourceNoCandidates(false);
        }
      } catch {
        if (!cancelled) {
          setBestResourceSuggestion(null);
          setBestResourceNoCandidates(false);
        }
      } finally {
        if (!cancelled) setBestResourceLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [
    showNewAppointmentModal,
    newAppointment.service_type_id,
    newAppointment.start_time,
    newAppointment.end_time,
    newAppointment.store_id,
    calendarUsers,
  ]);

  // Auto-apply best-resource suggestion when the user has not manually
  // overridden the assignee yet.
  useEffect(() => {
    if (!showNewAppointmentModal) return;
    if (naAssigneeDirty) return;
    if (!bestResourceSuggestion) return;
    if (newAppointment.assigned_to_id === bestResourceSuggestion.user_id) return;
    setNewAppointment(prev => ({ ...prev, assigned_to_id: bestResourceSuggestion.user_id }));
  }, [showNewAppointmentModal, naAssigneeDirty, bestResourceSuggestion, newAppointment.assigned_to_id]);

  // Auto-derive title as "{Tipo} – {Cliente}" while the user has not
  // manually edited it.
  useEffect(() => {
    if (!showNewAppointmentModal) return;
    if (naTitleDirty) return;
    const type = serviceTypes.find(t => t.id === newAppointment.service_type_id);
    const typeName = type?.name || '';
    const customerName = (newAppointment.customer_name || '').trim();
    let composed = '';
    if (typeName && customerName) composed = `${typeName} – ${customerName}`;
    else if (typeName) composed = typeName;
    else if (customerName) composed = customerName;
    setNewAppointment(prev => prev.title === composed ? prev : { ...prev, title: composed });
  }, [showNewAppointmentModal, naTitleDirty, newAppointment.service_type_id, newAppointment.customer_name, serviceTypes]);

  // Live slot-conflict check for the chosen type+slot+assignee.
  // Looks up the day's slots and surfaces an inline banner when the chosen
  // window overlaps an unavailable slot, suggesting the next free one.
  useEffect(() => {
    if (!showNewAppointmentModal) {
      setSlotConflict(null);
      return;
    }
    const { service_type_id, start_time, end_time, assigned_to_id } = newAppointment;
    if (!service_type_id || !start_time || !end_time) {
      setSlotConflict(null);
      return;
    }
    const startDate = new Date(start_time);
    const endDate = new Date(end_time);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate <= startDate) {
      setSlotConflict(null);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const dateStr = toLocalDateStr(startDate);
        const userParam = assigned_to_id ? `&user=${assigned_to_id}` : '';
        const res = await apiClient.get<CalendarTimeSlot[]>(`/calendar/slots/${dateStr}?type=${service_type_id}${userParam}`);
        if (cancelled || !res.success || !res.data) return;
        const slots = res.data;
        const startMs = startDate.getTime();
        const endMs = endDate.getTime();
        const conflict = slots.find(s => {
          if (s.available) return false;
          const sMs = new Date(s.start).getTime();
          const eMs = new Date(s.end).getTime();
          return sMs < endMs && eMs > startMs;
        });
        if (!conflict) {
          setSlotConflict(null);
          return;
        }
        const occ = conflict.appointments?.[0];
        const occupiedBy = occ?.customer_name || occ?.title || 'altro appuntamento';
        const nextFree = slots.find(s => s.available && new Date(s.start).getTime() >= startMs);
        setSlotConflict({
          occupiedBy,
          nextFree: nextFree ? { start: nextFree.start, end: nextFree.end } : null,
        });
      } catch {
        if (!cancelled) setSlotConflict(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [
    showNewAppointmentModal,
    newAppointment.service_type_id,
    newAppointment.start_time,
    newAppointment.end_time,
    newAppointment.assigned_to_id,
    newAppointment
  ]);

  // Task #649: carica i prodotti/servizi POS collegati al tipo di appuntamento
  // selezionato così che la sezione "Prodotto/Servizio" della modale possa
  // mostrarli (sia nel modale di creazione che in quello di modifica).
  useEffect(() => {
    const typeId = newAppointment.service_type_id;
    if (!typeId || (!showNewAppointmentModal && !showEditAppointmentModal)) {
      setLinkedProducts([]);
      return;
    }
    let cancelled = false;
    apiClient
      .get<Array<{ id: string; name: string; description: string | null; price: number | null }>>(
        `/calendar/types/${typeId}/services`,
      )
      .then((res) => {
        if (cancelled) return;
        if (res.success) setLinkedProducts(res.data || []);
      })
      .catch(() => { if (!cancelled) setLinkedProducts([]); });
    return () => { cancelled = true; };
  }, [newAppointment.service_type_id, showNewAppointmentModal, showEditAppointmentModal]);

  // Task #3040 — Sezione Completo: replica la logica del form pubblico in
  // modalità 'auto'. Con un solo prodotto collegato lo auto-selezioniamo (il
  // selettore resta nascosto); con più prodotti l'utente sceglie.
  useEffect(() => {
    if (!showNewAppointmentModal || naMode !== 'completo') return;
    const autoId = autoSelectSingleProduct('auto', linkedProducts);
    if (!autoId) return;
    if (newAppointment.product_id === autoId) return;
    const prod = linkedProducts.find((p) => p.id === autoId);
    setNewAppointment((prev) => ({
      ...prev,
      product_id: autoId,
      product_name: prod?.name || '',
      product_price: prod?.price ?? null,
    }));
  }, [showNewAppointmentModal, naMode, linkedProducts, newAppointment.product_id]);

  // Task #3040 — Eligibility per i filtri "competenze"/"turni" della rosa
  // "Assegnato a" (solo Completo). Forziamo lo skip del gate skill lato server
  // (force_missing_skills) così la valutazione turni viene comunque calcolata
  // per ogni candidato; i due flag derivati pilotano il filtro client-side.
  useEffect(() => {
    if (!showNewAppointmentModal || naMode !== 'completo' || (!naFilterSkills && !naFilterShifts)) {
      return;
    }
    const { service_type_id, start_time, end_time } = newAppointment;
    if (!service_type_id || !start_time || !end_time) {
      setNaEligibility({});
      return;
    }
    let cancelled = false;
    setNaEligibilityLoading(true);
    const handle = window.setTimeout(() => {
      apiClient
        .post<{
          candidates: Array<{
            user_id: string;
            required_skills_satisfied?: boolean;
            exclusion_reason?: string | null;
          }>;
        }>('/calendar/eligible-resources', {
          service_id: service_type_id,
          start: start_time,
          end: end_time,
          store_id: newAppointment.store_id || null,
          overrides: { force_missing_skills: true },
        })
        .then((res) => {
          if (cancelled) return;
          const map: Record<string, { skillsOk: boolean; shiftOk: boolean }> = {};
          const candidates = res.success ? res.data?.candidates ?? [] : [];
          for (const c of candidates) {
            const reason = c.exclusion_reason || '';
            const shiftOk = reason !== 'Risorsa non in turno per questo slot'
              && reason !== 'Risorsa con ferie/permesso approvato';
            map[c.user_id] = {
              skillsOk: c.required_skills_satisfied === true,
              shiftOk,
            };
          }
          setNaEligibility(map);
        })
        .catch(() => { if (!cancelled) setNaEligibility({}); })
        .finally(() => { if (!cancelled) setNaEligibilityLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(handle); };
  }, [
    showNewAppointmentModal,
    naMode,
    naFilterSkills,
    naFilterShifts,
    newAppointment.service_type_id,
    newAppointment.start_time,
    newAppointment.end_time,
    newAppointment.store_id,
  ]);

  // Task #3040 — rosa "Assegnato a" filtrata dai toggle competenze/turni.
  // I filtri sono attivi solo in Completo; l'operatore già selezionato resta
  // sempre presente per non perdere il valore corrente del Select.
  const naAssignableUsers = useMemo(() => {
    const all = calendarUsers.filter((u) => u.id);
    if (naMode !== 'completo' || (!naFilterSkills && !naFilterShifts)) return all;
    const selectedId = newAppointment.assigned_to_id || user?.id;
    return all.filter((u) => {
      if (u.id === selectedId) return true;
      const e = naEligibility[u.id];
      if (!e) return false;
      if (naFilterSkills && !e.skillsOk) return false;
      if (naFilterShifts && !e.shiftOk) return false;
      return true;
    });
  }, [calendarUsers, naMode, naFilterSkills, naFilterShifts, naEligibility, newAppointment.assigned_to_id, user?.id]);

  // Task #649: quando l'utente sceglie un tipo e non ha ancora pianificato
  // reminder manualmente, applichiamo un default sensato (24h email + 1h
  // whatsapp). Solo nel modale di creazione, per non sovrascrivere i
  // reminder già esistenti su un appuntamento in modifica.
  useEffect(() => {
    if (!showNewAppointmentModal) return;
    if (!newAppointment.service_type_id) return;
    if ((newAppointment.scheduled_reminders || []).length > 0) return;
    setNewAppointment((prev) => ({
      ...prev,
      scheduled_reminders: [
        { channel: 'email', offset_minutes: 1440 },
        { channel: 'whatsapp', offset_minutes: 60 },
      ],
    }));
  }, [newAppointment.service_type_id, showNewAppointmentModal, newAppointment.scheduled_reminders]);

  // Apply persisted user defaults when the modal opens.
  useEffect(() => {
    if (!showNewAppointmentModal) return;
    setNaTitleDirty(false);
    setNaAssigneeDirty(false);
    setSlotConflict(null);
    // Task #3040 — ripristina solo rapido/completo (la modalità Personale è
    // una scelta deliberata per-sessione, mai default all'apertura).
    let restoredMode: 'rapido' | 'completo' = 'rapido';
    try {
      const savedMode = localStorage.getItem('cal:naMode');
      restoredMode = savedMode === 'completo' ? 'completo' : 'rapido';
    } catch { /* ignore */ }
    setNaMode(restoredMode);
    setNaMoreOpen(restoredMode === 'completo');
    setNaPersonal({ title: '', description: '', start_time: '', end_time: '', all_day: false, color: '#0ea5e9' });
    try {
      const lastType = localStorage.getItem('cal:lastServiceType') || '';
      const lastStore = localStorage.getItem('cal:lastStoreId') || '';
      const lastRemindersRaw = localStorage.getItem('cal:lastReminderOffsets') || '';
      let lastReminders: number[] = [];
      if (lastRemindersRaw) {
        try {
          const parsed: unknown = JSON.parse(lastRemindersRaw);
          if (Array.isArray(parsed)) {
            lastReminders = parsed
              .filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0);
          }
        } catch { /* ignore */ }
      }
      if (!lastType && !lastStore && lastReminders.length === 0) return;
      setNewAppointment(prev => {
        const next = { ...prev };
        if (lastReminders.length > 0 && (!prev.reminder_offsets || prev.reminder_offsets.length === 0)) {
          next.reminder_offsets = lastReminders;
        }
        if (!prev.service_type_id && lastType) {
          next.service_type_id = lastType;
          if (prev.start_time) {
            const type = serviceTypes.find(t => t.id === lastType);
            if (type) {
              const start = new Date(prev.start_time);
              const end = new Date(start);
              end.setMinutes(end.getMinutes() + type.duration);
              next.end_time = toLocalDatetimeString(end);
            }
          }
        }
        if (!prev.store_id && lastStore) {
          const store = stores.find(s => s.id === lastStore);
          next.store_id = lastStore;
          if (store) {
            next.location = [store.name, store.address, store.city].filter(Boolean).join(', ');
          }
        }
        return next;
      });
    } catch { /* localStorage may be unavailable */ }
  }, [showNewAppointmentModal, serviceTypes, stores]);

  // Task #595: usa filteredUnifiedEvents così che il filtro sorgente
  // si propaghi alle liste eventi visibili e ai contatori header.
  const allDayEvents = useMemo(() => filteredUnifiedEvents.filter(e => e.all_day), [filteredUnifiedEvents]);
  const timedEvents = useMemo(() => filteredUnifiedEvents.filter(e => !e.all_day), [filteredUnifiedEvents]);

  const handleUnifiedEventClick = (event: UnifiedEvent) => {
    if (event.source === 'appointment' && event.metadata.appointmentId) {
      const apt = dayAppointments.find(a => a.id === event.metadata.appointmentId);
      if (apt) {
        setSelectedAppointment(apt);
        setShowAppointmentModal(true);
      }
    } else if (event.source === 'task') {
      setSelectedTaskEvent(event);
      setShowTaskModal(true);
    } else if (event.source === 'videocall') {
      setSelectedVideoEvent(event);
      setShowVideoModal(true);
    } else if (event.source === 'workgroup_event') {
      setSelectedWorkgroupEvent(event);
      setShowWorkgroupEventModal(true);
    }
  };

  const getPriorityBadge = (priority?: string) => {
    if (!priority) return null;
    const map: Record<string, { bg: string; text: string; label: string }> = {
      urgent: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Urgente' },
      high: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', label: 'Alta' },
      medium: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', label: 'Media' },
      low: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Bassa' },
    };
    const info = map[priority];
    if (!info) return null;
    return (
      <Badge className={cn('text-[10px] px-1.5 py-0 border', info.bg, info.text)}>
        {info.label}
      </Badge>
    );
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleToggleGoogleSync = async (enabled: boolean) => {
    if (!canManage) return;
    setTogglingGoogleSync(true);
    try {
      const res = await apiClient.put('/calendar/google-sync/toggle', { enabled });
      if (res.success) {
        setGoogleSyncStatus(prev => prev ? { ...prev, enabled } : null);
        toast({
          title: enabled ? 'Sincronizzazione attivata' : 'Sincronizzazione disattivata',
          description: enabled ? 'Gli appuntamenti verranno sincronizzati con Google Calendar' : 'La sincronizzazione con Google Calendar è stata disattivata'
        });
      }
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Impossibile aggiornare la sincronizzazione', variant: 'destructive' });
    } finally {
      setTogglingGoogleSync(false);
    }
  };

  const handleSyncAllGoogle = async () => {
    if (!canManage) return;
    setSyncingGoogle(true);
    try {
      const body: { account_id?: string } = {};
      if (selectedSyncAccountId) {
        body.account_id = selectedSyncAccountId;
      }
      const res = await apiClient.post<{ synced?: number; errors?: number; total?: number }>('/calendar/google-sync/sync-all', body);
      if (res.success) {
        const { synced = 0, errors = 0, total = 0 } = res.data ?? {};
        toast({
          title: 'Sincronizzazione completata',
          description: `${synced}/${total} appuntamenti sincronizzati${errors > 0 ? `, ${errors} errori` : ''}`
        });
        loadGoogleSyncStatus();
      }
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Sincronizzazione fallita', variant: 'destructive' });
    } finally {
      setSyncingGoogle(false);
    }
  };

  const handleSyncSingleGoogle = async (appointmentId: string) => {
    if (!canManage) return;
    const appointment = dayAppointments.find(a => a.id === appointmentId)
      ?? appointments.find(a => a.id === appointmentId)
      ?? (selectedAppointment?.id === appointmentId ? selectedAppointment : null);
    if (!appointment || !canEditAppointment(appointment)) return;
    setSyncingGoogle(true);
    try {
      const body: { account_id?: string } = {};
      if (selectedSyncAccountId) {
        body.account_id = selectedSyncAccountId;
      }
      const res = await apiClient.post(`/calendar/google-sync/sync/${appointmentId}`, body);
      if (res.success) {
        toast({ title: 'Sincronizzato', description: 'Appuntamento sincronizzato con Google Calendar' });
        loadDayData();
      }
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e) || 'Sincronizzazione fallita', variant: 'destructive' });
    } finally {
      setSyncingGoogle(false);
    }
  };

  const searchContacts = async (query: string) => {
    if (query.length < 2) {
      setContactResults([]);
      return;
    }
    setSearchingContacts(true);
    try {
      const res = await apiClient.get(`/crm/contacts/search?q=${encodeURIComponent(query)}&limit=10`);
      console.log('[Calendar] Contact search raw response:', res);
      
      // Handle both wrapped {success, data} and unwrapped {customers, leads} responses
      let customers: ContactSearchResult[] = [];
      let leads: ContactSearchResult[] = [];
      
      if (res && typeof res === 'object') {
        // Boundary: la risposta può arrivare wrappata `{success, data}` o
        // diretta `{customers, leads}` (legacy). Un solo cast al tipo union.
        const resAny = res as {
          success?: boolean;
          data?: { customers?: ContactSearchResult[]; leads?: ContactSearchResult[] };
          customers?: ContactSearchResult[];
          leads?: ContactSearchResult[];
        };
        if (resAny.success && resAny.data) {
          customers = resAny.data.customers || [];
          leads = resAny.data.leads || [];
        } else {
          // Direct response without wrapper
          customers = resAny.customers || [];
          leads = resAny.leads || [];
        }
      }
      
      const results = [...customers, ...leads];
      console.log('[Calendar] Parsed contacts:', results.length, 'results');
      setContactResults(results);
    } catch (error) {
      console.error('[Calendar] Error searching contacts:', error);
      setContactResults([]);
    } finally {
      setSearchingContacts(false);
    }
  };

  const handleContactSelect = (contact: ContactSearchResult) => {
    const displayName = contact.company_name || 
      `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    
    setNewAppointment(prev => ({
      ...prev,
      customer_name: displayName,
      customer_email: contact.email || '',
      customer_phone: contact.phone || '',
      customer_id: contact.contact_type === 'customer' ? contact.id : '',
      lead_id: contact.contact_type === 'lead' ? contact.id : ''
    }));
    setContactSearch(displayName);
    setShowContactDropdown(false);
    setContactResults([]);
  };

  const reconcileContact = async (field: 'email' | 'phone', value: string) => {
    if (!value || value.length < 3) return;
    try {
      const res = await apiClient.get(`/crm/contacts/search?q=${encodeURIComponent(value)}&limit=5`);
      // Boundary: risposta wrappata o diretta (vedi searchContacts).
      const resAny = res as {
        success?: boolean;
        data?: { customers?: ContactSearchResult[]; leads?: ContactSearchResult[] };
        customers?: ContactSearchResult[];
        leads?: ContactSearchResult[];
      };
      let customers: ContactSearchResult[] = [];
      let leads: ContactSearchResult[] = [];
      if (resAny?.success && resAny.data) {
        customers = resAny.data.customers || [];
        leads = resAny.data.leads || [];
      } else if (resAny) {
        customers = resAny.customers || [];
        leads = resAny.leads || [];
      }
      const match = [...customers, ...leads].find(c => 
        (field === 'email' && c.email?.toLowerCase() === value.toLowerCase()) ||
        (field === 'phone' && c.phone?.replace(/\s/g, '') === value.replace(/\s/g, ''))
      );
      if (match) {
        handleContactSelect(match);
        toast({ title: 'Contatto trovato', description: `${match.company_name || `${match.first_name || ''} ${match.last_name || ''}`.trim()} collegato automaticamente.` });
      }
    } catch {}
  };

  const handleCreateFastCustomer = async (customerType: 'b2c' | 'b2b') => {
    if (!canManage || !hasPermission('crm.manage')) return;
    const raw = (contactSearch || newAppointment.customer_name || '').trim();
    if (!raw) {
      toast({ title: 'Nome obbligatorio', description: 'Inserisci il nome del cliente prima di crearlo.', variant: 'destructive' });
      return;
    }
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
    const isPhone = /^\+?[\d\s().-]{6,}$/.test(raw) && /\d{6,}/.test(raw.replace(/\D/g, ''));
    let name = raw;
    let email = newAppointment.customer_email || '';
    let phone = newAppointment.customer_phone || '';
    if (isEmail) {
      email = raw;
      name = raw.split('@')[0]!.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    } else if (isPhone) {
      phone = raw;
      name = customerType === 'b2c' ? 'Nuovo contatto' : 'Nuova azienda';
    }
    try {
      const payload: {
        customer_type: 'b2c' | 'b2b';
        email?: string;
        phone?: string;
        skip_duplicate_check: boolean;
        first_name?: string;
        last_name?: string;
        company_name?: string;
      } = {
        customer_type: customerType,
        email: email || undefined,
        phone: phone || undefined,
        skip_duplicate_check: false
      };
      if (customerType === 'b2c') {
        const parts = name.split(' ');
        payload.first_name = parts[0] || name;
        payload.last_name = parts.slice(1).join(' ') || '';
      } else {
        payload.company_name = name;
      }
      const res = await apiClient.post('/crm/customers', payload);
      // Boundary: risposta wrappata `{success, data}` o cliente diretto.
      const resAny = res as { success?: boolean; data?: { id?: string } } & { id?: string };
      const customer = resAny?.success ? resAny.data : resAny;
      const customerId = customer?.id;
      if (customerId) {
        setNewAppointment(prev => ({ ...prev, customer_id: customerId, lead_id: '' }));
        toast({ title: 'Cliente creato', description: `${name} aggiunto come ${customerType === 'b2c' ? 'Contatto B2C' : 'Azienda B2B'} e collegato.` });
      }
    } catch (err) {
      if ((err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string; duplicate?: boolean; code?: string; missing_skills?: string[]; can_override?: boolean }; status?: number }; message?: string; code?: string; status?: number })?.response?.status === 409 || (err as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string; duplicate?: boolean; code?: string; missing_skills?: string[]; can_override?: boolean }; status?: number }; message?: string; code?: string; status?: number })?.response?.data?.duplicate) {
        toast({ title: 'Cliente esistente', description: 'Un cliente con questi dati esiste già. Usa la ricerca per collegarlo.', variant: 'destructive' });
      } else {
        toast({ title: 'Errore', description: 'Impossibile creare il cliente.', variant: 'destructive' });
      }
    }
  };

  const handleSlotClick = (slot: CalendarTimeSlot) => {
    if (slot.available) {
      if (!canManage) return;
      const startTime = new Date(slot.start);
      const endTime = new Date(slot.end);
      
      setPrefillCampaignId('');
      setNewAppointment({
        title: '',
        description: '',
        service_type_id: '',
        start_time: toLocalDatetimeString(startTime),
        end_time: toLocalDatetimeString(endTime),
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_id: '',
        lead_id: '',
        assigned_to_id: user?.id || '',
        observer_ids: [],
        campaign_id: '',
        status: 'scheduled',
        location: '',
        notes: '',
        internal_notes: '',
        store_id: '',
      priority: 'normal',
      reminder_offsets: [],
      source_kind: 'manuale_admin',
      appointment_form_id: '',
      form_values: {},
      product_id: '',
      product_name: '',
      product_price: null,
      product_quantity: 1,
      product_note: '',
      scheduled_reminders: [],
      });
      setContactSearch('');
      setContactResults([]);
      setNewAppointmentStep(1);
      setShowNewAppointmentModal(true);
    } else if (slot.appointments && slot.appointments.length > 0) {
      // Task #526: when the slot has multiple overlapping appointments,
      // open the first one (overflow chips/popover handle the rest).
      setSelectedAppointment(slot.appointments[0]!);
      setShowAppointmentModal(true);
    }
  };

  const submitCreateAppointment = async (overrideOpts?: { force_missing_skills?: boolean; override_reason?: string }) => {
    if (!canManage) return;
    if (!canWriteCalendarObject(access, { assigned_to_id: newAppointment.assigned_to_id || user?.id }, 'create')) return;
    const effectiveStatus = naMode === 'rapido' ? 'scheduled' : newAppointment.status;
    // Task #649: build a structured payload that maps the new "Più opzioni"
    // fields to the right backend columns and to `metadata.*` so the
    // backend trigger can derive `source_kind` and the detail panel can
    // display priority / form values / product / scheduled reminders.
    const productMeta: Record<string, unknown> = {};
    if (newAppointment.product_id) {
      productMeta.product_id = newAppointment.product_id;
      productMeta.product_name = newAppointment.product_name || undefined;
      productMeta.product_price = newAppointment.product_price ?? undefined;
      productMeta.product_quantity = newAppointment.product_quantity || 1;
      if (newAppointment.product_note) productMeta.product_note = newAppointment.product_note;
    }
    const metadata: Record<string, unknown> = {
      priority: newAppointment.priority,
      source_kind: newAppointment.source_kind,
      ...productMeta,
    };
    if (newAppointment.appointment_form_id && Object.keys(newAppointment.form_values || {}).length > 0) {
      metadata.form_values = newAppointment.form_values;
    }
    if (Array.isArray(newAppointment.scheduled_reminders) && newAppointment.scheduled_reminders.length > 0) {
      metadata.scheduled_reminders = newAppointment.scheduled_reminders;
    }
    const payload: Omit<typeof newAppointment, 'appointment_form_id' | 'status'> & {
      status: string;
      user_id: string | undefined;
      appointment_form_id: string | null;
      metadata: Record<string, unknown>;
      force_missing_skills?: boolean;
      override_reason?: string;
    } = {
      ...newAppointment,
      status: effectiveStatus,
      user_id: (selectedUserId && selectedUserId !== 'all') ? selectedUserId : user?.id,
      appointment_form_id: newAppointment.appointment_form_id || null,
      metadata,
    };
    if (overrideOpts?.force_missing_skills) {
      payload.force_missing_skills = true;
      payload.override_reason = overrideOpts.override_reason;
    }
    const response = await apiClient.post<CalendarAppointment>('/calendar/appointments', payload);
    if (response.success) {
      try {
        if (newAppointment.service_type_id) localStorage.setItem('cal:lastServiceType', newAppointment.service_type_id);
        if (newAppointment.store_id) localStorage.setItem('cal:lastStoreId', newAppointment.store_id);
        if (newAppointment.reminder_offsets && newAppointment.reminder_offsets.length > 0) {
          localStorage.setItem('cal:lastReminderOffsets', JSON.stringify(newAppointment.reminder_offsets));
        }
        localStorage.setItem('cal:naMode', naMode === 'completo' ? 'completo' : 'rapido');
      } catch { /* localStorage may be unavailable */ }
      if (includeVideoMeeting && response.data?.id) {
        try {
          await apiClient.post('/video/rooms', {
            title: `Video: ${newAppointment.title}`,
            appointmentId: response.data.id,
          });
        } catch (e) { /* video room creation is optional */ }
      }
      toast({
        title: 'Successo',
        description: includeVideoMeeting ? 'Appuntamento con video meeting creato' : 'Appuntamento creato con successo',
      });
      setIncludeVideoMeeting(false);
      setShowNewAppointmentModal(false);
      loadDayData();
      loadUnifiedDayData();
      loadMonthData();
      loadUnifiedMonthData();
    }
  };

  // Task #3040 — creazione evento personale (privato dell'utente, tabella
  // dedicata calendar_personal_events; nessun reminder/cliente/sede).
  const handleCreatePersonalEvent = async () => {
    if (!canManage) return;
    if (!naPersonal.title.trim() || !naPersonal.start_time || !naPersonal.end_time) {
      toast({
        title: 'Errore',
        description: 'Compila Titolo, Inizio e Fine',
        variant: 'destructive',
      });
      return;
    }
    const toIso = (value: string, endOfDay: boolean): string => {
      if (naPersonal.all_day) {
        const d = new Date(`${value}T00:00:00`);
        if (endOfDay) d.setHours(23, 59, 59, 0);
        return d.toISOString();
      }
      return new Date(value).toISOString();
    };
    const startIso = toIso(naPersonal.start_time, false);
    const endIso = toIso(naPersonal.end_time, true);
    if (new Date(endIso) <= new Date(startIso)) {
      toast({
        title: 'Errore',
        description: 'La fine deve essere successiva all\'inizio',
        variant: 'destructive',
      });
      return;
    }
    setSavingPersonalEvent(true);
    try {
      const res = await apiClient.post('/calendar/personal-events', {
        title: naPersonal.title.trim(),
        description: naPersonal.description.trim() || null,
        start_time: startIso,
        end_time: endIso,
        all_day: naPersonal.all_day,
        color: naPersonal.color,
      });
      if (res.success) {
        toast({ title: 'Successo', description: 'Evento personale creato' });
        setShowNewAppointmentModal(false);
        loadDayData();
        loadUnifiedDayData();
        loadMonthData();
        loadUnifiedMonthData();
      } else {
        toast({ title: 'Errore', description: res.error || 'Creazione non riuscita', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Errore', description: 'Creazione non riuscita', variant: 'destructive' });
    } finally {
      setSavingPersonalEvent(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!canManage) return;
    if (!newAppointment.service_type_id || !newAppointment.title || !newAppointment.start_time || !newAppointment.end_time) {
      toast({
        title: 'Errore',
        description: 'Compila i campi obbligatori (Tipo, Titolo, Inizio, Fine)',
        variant: 'destructive'
      });
      return;
    }

    if (!newAppointment.store_id) {
      toast({
        title: 'Sede mancante',
        description: 'Seleziona la sede a cui associare l\'appuntamento.',
        variant: 'destructive'
      });
      return;
    }

    const now = new Date();
    const startTime = new Date(newAppointment.start_time);
    const endTime = new Date(newAppointment.end_time);

    if (startTime < now) {
      toast({
        title: 'Errore',
        description: 'Non è possibile creare appuntamenti nel passato',
        variant: 'destructive'
      });
      return;
    }

    if (endTime <= startTime) {
      toast({
        title: 'Errore',
        description: 'L\'orario di fine deve essere successivo all\'orario di inizio',
        variant: 'destructive'
      });
      return;
    }

    // Task #649: validazione campi modulo (custom_fields) richiesti.
    if (newAppointment.appointment_form_id) {
      const form = appointmentForms.find((f) => f.id === newAppointment.appointment_form_id);
      const missing: string[] = [];
      for (const cf of (form?.custom_fields || [])) {
        if (!cf.required) continue;
        const v = newAppointment.form_values?.[cf.id];
        const empty = cf.type === 'checkbox'
          ? v !== true
          : (typeof v !== 'string' || v.trim() === '');
        if (empty) missing.push(cf.label);
      }
      if (missing.length > 0) {
        toast({
          title: 'Campi modulo mancanti',
          description: `Compila i campi richiesti: ${missing.join(', ')}`,
          variant: 'destructive',
        });
        return;
      }
    }

    setSavingAppointment(true);
    try {
      await submitCreateAppointment();
    } catch (error) {
      const data = (error as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string; duplicate?: boolean; code?: string; missing_skills?: string[]; can_override?: boolean }; status?: number }; message?: string; code?: string; status?: number })?.response?.data;
      if (data?.code === 'MISSING_SKILLS') {
        setSkillOverrideReason('');
        setSkillOverrideDialog({
          open: true,
          mode: 'create',
          missingSkills: data.missing_skills || [],
          canOverride: !!data.can_override,
          message: (typeof data.error === 'string' ? data.error : data.error?.message) || getErrorMessage(error) || '',
        });
      } else {
        toast({
          title: 'Errore',
          description: getErrorMessage(error) || 'Impossibile creare l\'appuntamento',
          variant: 'destructive'
        });
      }
    } finally {
      setSavingAppointment(false);
    }
  };

  const handleUpdateAppointmentStatus = async (appointmentId: string, status: string) => {
    if (!canManage) return;
    const appointment = dayAppointments.find(a => a.id === appointmentId)
      ?? appointments.find(a => a.id === appointmentId)
      ?? (selectedAppointment?.id === appointmentId ? selectedAppointment : null);
    if (!appointment || !canEditAppointment(appointment)) return;
    try {
      const response = await apiClient.put<CalendarAppointment>(`/calendar/appointments/${appointmentId}`, { status });
      if (response.success) {
        toast({
          title: 'Successo',
          description: 'Stato aggiornato'
        });
        loadDayData();
        loadUnifiedDayData();
        loadMonthData();
        loadUnifiedMonthData();
        setShowAppointmentModal(false);
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile aggiornare lo stato',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    if (!canManage) return;
    const appointment = dayAppointments.find(a => a.id === appointmentId)
      ?? appointments.find(a => a.id === appointmentId)
      ?? (selectedAppointment?.id === appointmentId ? selectedAppointment : null);
    if (!appointment || !canDeleteAppointment(appointment)) return;
    if (!confirm('Sei sicuro di voler eliminare questo appuntamento?')) return;

    try {
      const response = await apiClient.delete<void>(`/calendar/appointments/${appointmentId}`);
      if (response.success) {
        toast({
          title: 'Successo',
          description: 'Appuntamento eliminato'
        });
        setShowAppointmentModal(false);
        setSelectedAppointment(null);
        loadDayData();
        loadUnifiedDayData();
        loadMonthData();
        loadUnifiedMonthData();
      }
    } catch (error) {
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Impossibile eliminare l\'appuntamento',
        variant: 'destructive'
      });
    }
  };

  const handleOpenEditAppointment = (appointment: CalendarAppointment) => {
    if (!canEditAppointment(appointment)) return;
    setEditingAppointmentId(appointment.id);
    const toLocalDT = (iso: string | Date) => {
      const d = new Date(iso);
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    // Task #3694: `metadata` è `unknown` sul DTO shared → narrowing tipato per
    // i campi che questa UI legge (nessun cambiamento runtime rispetto ai
    // vecchi accessi non tipizzati a `metadata?.X`).
    const md = (appointment.metadata && typeof appointment.metadata === 'object'
      ? appointment.metadata as Record<string, unknown>
      : {});
    setNewAppointment({
      title: appointment.title || '',
      description: appointment.description || '',
      service_type_id: appointment.service_type_id || '',
      start_time: toLocalDT(appointment.start_time),
      end_time: toLocalDT(appointment.end_time),
      customer_name: appointment.customer_name || '',
      customer_email: appointment.customer_email || '',
      customer_phone: appointment.customer_phone || '',
      customer_id: appointment.customer_id || '',
      lead_id: appointment.lead_id || '',
      assigned_to_id: appointment.assigned_to_id || '',
      observer_ids: appointment.observer_ids || [],
      campaign_id: appointment.campaign_id || '',
      status: (appointment.status || 'scheduled') as AppointmentStatus,
      location: appointment.location || '',
      notes: appointment.notes || '',
      internal_notes: appointment.internal_notes || '',
      store_id: appointment.store_id || '',
      priority: ((md.priority as string) || 'normal') as 'low' | 'normal' | 'high' | 'urgent',
      // `reminder_offsets` non è una colonna del DTO: resta letto in modo
      // difensivo dal record grezzo (passthrough) senza `any` bloccante.
      reminder_offsets: Array.isArray((appointment as { reminder_offsets?: unknown }).reminder_offsets)
        ? (appointment as { reminder_offsets?: number[] }).reminder_offsets ?? []
        : [],
      source_kind: ((appointment.source_kind || (md.source_kind as string)) || 'manuale_admin') as AppointmentSourceKind,
      appointment_form_id: appointment.appointment_form_id || '',
      form_values: (md.form_values || {}) as Record<string, string | boolean>,
      product_id: (md.product_id as string) || '',
      product_name: (md.product_name as string) || '',
      product_price: (md.product_price as number | null) ?? null,
      product_quantity: (md.product_quantity as number) || 1,
      product_note: (md.product_note as string) || '',
      scheduled_reminders: Array.isArray(md.scheduled_reminders)
        ? (md.scheduled_reminders as Array<{ channel: 'email' | 'sms' | 'whatsapp'; offset_minutes: number }>)
        : [],
    });
    setShowAppointmentModal(false);
    setShowEditAppointmentModal(true);
  };

  const submitUpdateAppointment = async (overrideOpts?: { force_missing_skills?: boolean; override_reason?: string }) => {
    if (!canManage) return;
    const appointment = dayAppointments.find(a => a.id === editingAppointmentId)
      ?? appointments.find(a => a.id === editingAppointmentId)
      ?? (selectedAppointment?.id === editingAppointmentId ? selectedAppointment : null);
    if (!appointment || !canEditAppointment(appointment)) return;
    if (newAppointment.assigned_to_id && newAppointment.assigned_to_id !== appointment.assigned_to_id
      && !canWriteCalendarObject(access, { assigned_to_id: newAppointment.assigned_to_id }, 'edit')) return;
    // Task #649: keep parity with create — push priority / source / form /
    // product / scheduled reminders into metadata so they survive an edit.
    // Task #649: in MODIFICA inviamo TUTTE le chiavi gestite da questa UI
    // (anche con `null`/array vuoto) così che il merge JSONB lato DB
    // rifletta le rimozioni dell'utente (es. ho tolto il prodotto / ho
    // svuotato i reminder). Gli altri metadata estranei restano preservati
    // dal merge perché non li tocchiamo.
    const hasProduct = !!newAppointment.product_id;
    const productMeta: Record<string, unknown> = {
      product_id: hasProduct ? newAppointment.product_id : null,
      product_name: hasProduct ? (newAppointment.product_name || null) : null,
      product_price: hasProduct ? (newAppointment.product_price ?? null) : null,
      product_quantity: hasProduct ? (newAppointment.product_quantity || 1) : null,
      product_note: hasProduct ? (newAppointment.product_note || null) : null,
    };
    const metadata: Record<string, unknown> = {
      priority: newAppointment.priority,
      source_kind: newAppointment.source_kind,
      ...productMeta,
    };
    metadata.form_values = newAppointment.appointment_form_id ? (newAppointment.form_values || {}) : {};
    metadata.scheduled_reminders = Array.isArray(newAppointment.scheduled_reminders)
      ? newAppointment.scheduled_reminders : [];
    const payload: Omit<typeof newAppointment, 'appointment_form_id'> & {
      user_id: string | undefined;
      appointment_form_id: string | null;
      metadata: Record<string, unknown>;
      force_missing_skills?: boolean;
      override_reason?: string;
    } = {
      ...newAppointment,
      user_id: newAppointment.assigned_to_id || user?.id,
      appointment_form_id: newAppointment.appointment_form_id || null,
      metadata,
    };
    if (overrideOpts?.force_missing_skills) {
      payload.force_missing_skills = true;
      payload.override_reason = overrideOpts.override_reason;
    }
    const response = await apiClient.put<CalendarAppointment>(`/calendar/appointments/${editingAppointmentId}`, payload);
    if (response.success) {
      toast({ title: 'Successo', description: 'Appuntamento aggiornato' });
      setShowEditAppointmentModal(false);
      setEditingAppointmentId(null);
      loadDayData();
      loadUnifiedDayData();
      loadMonthData();
      loadUnifiedMonthData();
    }
  };

  const handleUpdateAppointment = async () => {
    if (!canManage) return;
    if (!editingAppointmentId || !newAppointment.title || !newAppointment.start_time || !newAppointment.end_time) {
      toast({ title: 'Errore', description: 'Compila tutti i campi obbligatori', variant: 'destructive' });
      return;
    }
    setSavingAppointment(true);
    try {
      await submitUpdateAppointment();
    } catch (error) {
      const data = (error as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string; duplicate?: boolean; code?: string; missing_skills?: string[]; can_override?: boolean }; status?: number }; message?: string; code?: string; status?: number })?.response?.data;
      if (data?.code === 'MISSING_SKILLS') {
        setSkillOverrideReason('');
        setSkillOverrideDialog({
          open: true,
          mode: 'update',
          missingSkills: data.missing_skills || [],
          canOverride: !!data.can_override,
          message: (typeof data.error === 'string' ? data.error : data.error?.message) || getErrorMessage(error) || '',
        });
      } else {
        toast({ title: 'Errore', description: getErrorMessage(error) || 'Impossibile aggiornare l\'appuntamento', variant: 'destructive' });
      }
    } finally {
      setSavingAppointment(false);
    }
  };

  const confirmSkillOverride = async () => {
    const reason = skillOverrideReason.trim();
    if (reason.length < 3) {
      toast({ title: 'Motivazione richiesta', description: 'Inserisci una motivazione (min. 3 caratteri).', variant: 'destructive' });
      return;
    }
    setSubmittingOverride(true);
    try {
      if (skillOverrideDialog.mode === 'create') {
        await submitCreateAppointment({ force_missing_skills: true, override_reason: reason });
      } else {
        await submitUpdateAppointment({ force_missing_skills: true, override_reason: reason });
      }
      setSkillOverrideDialog(s => ({ ...s, open: false }));
    } catch (error) {
      toast({
        title: 'Errore',
        description: (error as { response?: { data?: { error?: { message?: string; code?: string } | string; message?: string; duplicate?: boolean; code?: string; missing_skills?: string[]; can_override?: boolean }; status?: number }; message?: string; code?: string; status?: number })?.message || 'Impossibile salvare con override',
        variant: 'destructive',
      });
    } finally {
      setSubmittingOverride(false);
    }
  };

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, [currentDate]);

  const formatTime = (isoString: string | Date) => {
    return new Date(isoString).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date: Date) => {
    return `${date.getDate()} ${MONTHS_IT[date.getMonth()]} ${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <HubShell
      icon={CalendarIcon}
      title="Calendario"
      subtitle="Appuntamenti, task e video call in un unico posto"
      headerExtra={<div className="flex items-center gap-2"><AvailabilityModeBadge />
        {calendarPath && <Button variant="ghost" size="sm" onClick={() => navigate(`${calendarPath}/kb`)}>Guida Calendario</Button>}
      </div>}
      tabs={CALENDAR_MAIN_TABS
        .map(t => (t.key === 'inbox' ? { ...t, badge: inboxCount } : t))}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      fullHeight={false}
    >
        {bootstrapReady && !canManage && <p className="px-3 md:px-6 py-2 text-sm text-muted-foreground">
          Sola lettura: per creare o modificare appuntamenti ed eventi personali è richiesto calendar.manage.
        </p>}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsContent value="calendar" className="mt-0 px-3 md:px-6 pt-4">
        <div className="mb-4 sm:mb-6">
          {/* Filtri allineati a sinistra (titolo/badge/sottotitolo vivono nella
              testata condivisa sopra il menu).
              Toolbar filtri rivista:
              - I filtri sono raggruppati in un cluster pillola (stessa altezza,
                stesso bg, divisori sottili) per dare un look professionale e
                ridurre il rumore visivo dei tanti dropdown affiancati.
              - L'integrazione Google Calendar non è più una scatola con
                etichetta lunga: è un toggle compatto con icona ufficiale e
                tooltip esplicativo, allineato agli altri controlli.
              - "Sync con Google" e "Nuovo Appuntamento" diventano shortcut
                a icona (con tooltip), separati dal cluster filtri da una
                colonna sottile. Restano accessibili e identificabili senza
                rubare spazio orizzontale alla griglia. */}
          <TooltipProvider delayDuration={150}>
            <div className="flex flex-wrap items-center gap-2">
              {/* Mobile filter Sheet (desktop pill cluster below uses md:flex) */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="md:hidden h-9 gap-1.5 text-xs"
                  >
                    <FilterIcon className="w-3.5 h-3.5" />
                    Filtri
                    {(sourceKindsFilter.length > 0 || googleAccountFilter !== 'all' || (selectedUserId && selectedUserId !== 'all')) && (
                      <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] font-bold rounded-full bg-blue-500 text-white">
                        {sourceKindsFilter.length + (googleAccountFilter !== 'all' ? 1 : 0) + ((selectedUserId && selectedUserId !== 'all') ? 1 : 0)}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-auto max-h-[80vh] overflow-y-auto">
                  <SheetHeader className="mb-4">
                    <SheetTitle>Filtri calendario</SheetTitle>
                  </SheetHeader>
                  <div className="space-y-3">
                    {stores.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Sede</label>
                        <Select value={selectedStoreId} onValueChange={(v) => setSelectedStoreId(v)}>
                          <SelectTrigger className="h-10 w-full">
                            <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                            <SelectValue placeholder="Seleziona sede" />
                          </SelectTrigger>
                          <SelectContent>
                            {[...stores].filter(s => s.id).sort((a, b) =>
                              String(a.name || '').localeCompare(String(b.name || ''), 'it', { sensitivity: 'base' })
                            ).map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}{s.city ? ` - ${s.city}` : ''}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isBroad && calendarUsers.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Utente</label>
                        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                          <SelectTrigger className="h-10 w-full">
                            <User className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                            <SelectValue placeholder="Seleziona utente" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutti gli utenti</SelectItem>
                            {calendarUsers.filter(u => u.id).map((u) => (
                              <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Sorgenti appuntamento</label>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-60 overflow-y-auto">
                        {APPOINTMENT_SOURCE_TAXONOMY.map(t => {
                          const checked = sourceKindsFilter.includes(t.kind);
                          return (
                            <button
                              key={t.kind}
                              type="button"
                              onClick={() => {
                                setSourceKindsFilter(prev =>
                                  checked ? prev.filter(k => k !== t.kind) : [...prev, t.kind]
                                );
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-accent text-left ${checked ? 'bg-accent/60' : ''}`}
                            >
                              <Checkbox checked={checked} className="pointer-events-none" />
                              <AppointmentSourceBadge kind={t.kind} iconOnly />
                              <span className="flex-1 truncate">{t.label}</span>
                            </button>
                          );
                        })}
                      </div>
                      {sourceKindsFilter.length > 0 && (
                        <button
                          type="button"
                          className="mt-2 text-xs text-primary hover:underline"
                          onClick={() => setSourceKindsFilter([])}
                        >Azzera sorgenti</button>
                      )}
                    </div>
                    {isAdmin && googleSyncStatus?.enabled && (googleSyncStatus.google_accounts?.length || 0) > 1 && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">Calendario Google</label>
                        <Select value={googleAccountFilter} onValueChange={setGoogleAccountFilter}>
                          <SelectTrigger className="h-10 w-full">
                            <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                            <SelectValue placeholder="Tutti i calendari" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tutti i calendari</SelectItem>
                            {googleSyncStatus.google_accounts?.map(acc => (
                              <SelectItem key={acc.id} value={acc.account_email || acc.id}>
                                {acc.account_email || acc.account_label || acc.id.slice(0, 8)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              {/* Cluster filtri: pillola unica con divisori interni — desktop only */}
              <div className="hidden md:flex items-center gap-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden h-9">
                {stores.length > 0 && (
                  <Select value={selectedStoreId} onValueChange={(v) => setSelectedStoreId(v)}>
                    <SelectTrigger className="h-9 w-[150px] sm:w-[180px] border-0 rounded-none bg-transparent text-xs sm:text-sm text-gray-700 dark:text-gray-200">
                      <MapPin className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                      <SelectValue placeholder="Seleziona sede" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...stores].filter(s => s.id).sort((a, b) =>
                        String(a.name || '').localeCompare(String(b.name || ''), 'it', { sensitivity: 'base' })
                      ).map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{s.city ? ` - ${s.city}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {isBroad && calendarUsers.length > 0 && (
                  <>
                    {stores.length > 0 && <div className="w-px h-5 bg-gray-200 dark:bg-gray-700" />}
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger className="h-9 w-[150px] sm:w-[180px] border-0 rounded-none bg-transparent text-xs sm:text-sm text-gray-700 dark:text-gray-200">
                        <User className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                        <SelectValue placeholder="Seleziona utente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti gli utenti</SelectItem>
                        {calendarUsers.filter(u => u.id).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name || u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
                <div className="w-px h-5 bg-gray-200 dark:bg-gray-700" />
                {/* Task #595: filtro multi-select tassonomia sorgenti (9 tipi). */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-9 px-2 sm:px-3 text-xs sm:text-sm font-normal text-gray-700 dark:text-gray-200 rounded-none gap-1"
                    >
                      <span className="truncate">
                        {sourceKindsFilter.length === 0
                          ? 'Tutte le sorgenti'
                          : sourceKindsFilter.length === 1
                            ? (APPOINTMENT_SOURCE_TAXONOMY.find(t => t.kind === sourceKindsFilter[0])?.label || sourceKindsFilter[0])
                            : `${sourceKindsFilter.length} sorgenti`}
                      </span>
                      <ChevronDown className="w-3 h-3 opacity-60" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-1" align="start">
                    <div className="flex items-center justify-between px-2 py-1.5">
                      <span className="text-xs font-medium text-muted-foreground">Filtra per sorgente</span>
                      {sourceKindsFilter.length > 0 && (
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={() => setSourceKindsFilter([])}
                        >Azzera</button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {APPOINTMENT_SOURCE_TAXONOMY.map(t => {
                        const checked = sourceKindsFilter.includes(t.kind);
                        return (
                          <button
                            key={t.kind}
                            type="button"
                            onClick={() => {
                              setSourceKindsFilter(prev =>
                                checked ? prev.filter(k => k !== t.kind) : [...prev, t.kind]
                              );
                            }}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-accent text-left ${
                              checked ? 'bg-accent/60' : ''
                            }`}
                          >
                            <Checkbox checked={checked} className="pointer-events-none" />
                            <AppointmentSourceBadge kind={t.kind} iconOnly />
                            <span className="flex-1 truncate">{t.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
                {isAdmin && googleSyncStatus?.enabled && (googleSyncStatus.google_accounts?.length || 0) > 1 && (
                  <>
                    <div className="w-px h-5 bg-gray-200 dark:bg-gray-700" />
                    <Select value={googleAccountFilter} onValueChange={setGoogleAccountFilter}>
                      <SelectTrigger className="h-9 w-[160px] sm:w-[180px] border-0 rounded-none bg-transparent text-xs sm:text-sm text-gray-700 dark:text-gray-200">
                        <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                        <SelectValue placeholder="Tutti i calendari" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutti i calendari</SelectItem>
                        {googleSyncStatus.google_accounts?.map(acc => (
                          <SelectItem key={acc.id} value={acc.account_email || acc.id}>
                            {acc.account_email || acc.account_label || acc.id.slice(0, 8)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>

              {/* Shortcut Google Calendar: toggle con icona ufficiale + tooltip
                  esplicativo. Mostra anche un dot di stato (verde=connesso,
                  ambra=non connesso) per leggibilità a colpo d'occhio. */}
              {canManage && googleSyncStatus && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 h-9 px-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                      <div className="relative">
                        <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white dark:ring-gray-800 ${
                            googleSyncStatus.google_connected
                              ? googleSyncStatus.enabled ? 'bg-emerald-500' : 'bg-gray-400'
                              : 'bg-amber-500'
                          }`}
                        />
                      </div>
                      <Switch
                        checked={googleSyncStatus.enabled}
                        onCheckedChange={handleToggleGoogleSync}
                        disabled={togglingGoogleSync || !googleSyncStatus.google_connected}
                        className="scale-75 -mx-1"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px]">
                    <p className="font-medium mb-0.5">Sincronizzazione Google Calendar</p>
                    <p className="text-xs text-text-secondary">
                      {googleSyncStatus.google_connected
                        ? googleSyncStatus.enabled
                          ? 'Attiva: i nuovi appuntamenti vengono creati anche su Google Calendar.'
                          : 'Disattivata: gli appuntamenti restano solo qui.'
                        : 'Account Google non collegato. Connettilo dalle Impostazioni → Integrazioni.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Selettore account predefinito quando ci sono più account Google
                  collegati e la sync è attiva (rimane visibile solo nei casi
                  in cui serve davvero). */}
              {isAdmin && googleSyncStatus?.enabled && (googleSyncStatus.google_accounts?.length || 0) > 1 && (
                <Select value={selectedSyncAccountId || '__default__'} onValueChange={(v) => setSelectedSyncAccountId(v === '__default__' ? '' : v)}>
                  <SelectTrigger className="h-9 w-[180px] text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm">
                    <Mail className="w-3.5 h-3.5 mr-1.5 text-gray-500" />
                    <SelectValue placeholder="Account predefinito" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Account predefinito</SelectItem>
                    {googleSyncStatus.google_accounts?.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.account_email || acc.account_label || acc.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Shortcut "Sync ora" — solo icona, abilitato quando la sync è
                  attiva. Tooltip esplicativo. */}
              {canManage && googleSyncStatus?.enabled && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleSyncAllGoogle}
                      disabled={!canManage || syncingGoogle}
                      className="h-9 w-9 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50"
                    >
                      {syncingGoogle ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Sincronizza ora con Google Calendar
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Task #560: shortcut persistente "Apri in Planning" */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleTabChange('planning')}
                    className="h-9 w-9 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50"
                    aria-label="Apri in Planning"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Apri in vista Planning</TooltipContent>
              </Tooltip>

              {/* Shortcut "Nuovo appuntamento" — bottone primario compatto
                  (solo icona) con tooltip. Resta riconoscibile grazie al
                  colore primario. */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={!canManage}
                    onClick={() => {
                      if (!canManage) return;
                      const now = new Date();
                      const startTime = new Date(selectedDate);
                      startTime.setHours(now.getHours() + 1, 0, 0, 0);
                      const endTime = new Date(startTime);
                      endTime.setMinutes(endTime.getMinutes() + (settings?.slot_duration || 30));

                      setPrefillCampaignId('');
                      setNewAppointment({
                        title: '',
                        description: '',
                        service_type_id: '',
                        start_time: toLocalDatetimeString(startTime),
                        end_time: toLocalDatetimeString(endTime),
                        customer_name: '',
                        customer_email: '',
                        customer_phone: '',
                        customer_id: '',
                        lead_id: '',
                        assigned_to_id: user?.id || '',
                        observer_ids: [],
                        campaign_id: '',
                        status: 'scheduled',
                        location: '',
                        notes: '',
                        internal_notes: '',
                        store_id: '',
                      priority: 'normal',
                      reminder_offsets: [],
                      source_kind: 'manuale_admin',
                      appointment_form_id: '',
                      form_values: {},
                      product_id: '',
                      product_name: '',
                      product_price: null,
                      product_quantity: 1,
                      product_note: '',
                      scheduled_reminders: [],
                      });
                      setContactSearch('');
                      setContactResults([]);
                      setNewAppointmentStep(1);
                      setShowNewAppointmentModal(true);
                    }}
                    size="icon"
                    className="h-9 w-9 bg-purple-600 hover:bg-purple-700 text-white shadow-sm"
                    aria-label="Nuovo appuntamento"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Nuovo appuntamento</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-1">
            <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={isMobile ? () => {
                      const prev = new Date(selectedDate);
                      prev.setDate(prev.getDate() - 1);
                      setSelectedDate(prev);
                    } : handlePrevMonth}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center gap-2 cursor-pointer md:cursor-default" onClick={() => isMobile && setMobileCalendarExpanded(!mobileCalendarExpanded)}>
                    <CardTitle className="text-gray-900 dark:text-white text-lg">
                      {isMobile && !mobileCalendarExpanded
                        ? `${selectedDate.getDate()} ${MONTHS_IT[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
                        : `${MONTHS_IT[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                      }
                    </CardTitle>
                    {isMobile && (
                      mobileCalendarExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={isMobile ? () => {
                      const next = new Date(selectedDate);
                      next.setDate(next.getDate() + 1);
                      setSelectedDate(next);
                    } : handleNextMonth}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className={`${isMobile && !mobileCalendarExpanded ? 'hidden' : ''}`}>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {DAYS_IT.map((day) => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((date, idx) => {
                    if (!date) {
                      return <div key={`empty-${idx}`} className="h-10" />;
                    }
                    
                    const dateStr = toLocalDateStr(date);
                    const count = monthCounts[dateStr] || 0;
                    const uCounts = unifiedMonthCounts[dateStr];
                    const isSelected = date.toDateString() === selectedDate.toDateString();
                    const isToday = date.toDateString() === new Date().toDateString();
                    const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
                    const isWorkDay = settings?.days_available?.includes(dayOfWeek) ?? true;
                    
                    return (
                      <button
                        key={dateStr}
                        onClick={() => handleDateSelect(date)}
                        className={cn(
                          'relative h-10 rounded-lg transition-all duration-200 flex flex-col items-center justify-center',
                          isSelected
                            ? 'bg-purple-600 text-white'
                            : isToday
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-700'
                              : isWorkDay
                                ? 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                                : 'text-gray-400 dark:text-gray-600 opacity-50'
                        )}
                      >
                        <span className="text-sm font-medium">{date.getDate()}</span>
                        {(() => {
                          const shiftGroups = isModuleEnabled('hr') ? (monthShifts[dateStr] || []) : [];
                          const shiftPeopleCount = shiftGroups.reduce((acc, s) => acc + s.people.length, 0);
                          const shiftColor = shiftGroups[0]?.shift_color || '#3B82F6';
                          // Task #560: mini-calendario raffinato. Sostituiamo lo
                          // stack di pallini con un singolo indicatore di
                          // intensità (cerchio centrale con opacità proporzionale)
                          // più una barra a segmenti colorati che indica la
                          // presenza di tipologie multiple.
                          // Task #3694: i conteggi unified arrivano come union
                          // string|number (COUNT(*) pg = stringa sul wire) → li
                          // coerciamo a numero al consumo, il rendering resta
                          // identico (somma/confronto numerici).
                          const uAppointments = uCounts ? Number(uCounts.appointments) : 0;
                          const uTasks = uCounts ? Number(uCounts.tasks) : 0;
                          const uVideocalls = uCounts ? Number(uCounts.videocalls) : 0;
                          const uWorkgroupEvents = uCounts ? Number(uCounts.workgroup_events) : 0;
                          const totalEvents = uCounts
                            ? (uAppointments + uTasks + uVideocalls + uWorkgroupEvents)
                            : count;
                          const intensity = totalEvents === 0
                            ? 0
                            : totalEvents <= 1 ? 0.25
                            : totalEvents <= 3 ? 0.5
                            : totalEvents <= 6 ? 0.75
                            : 1;
                          const segments: string[] = [];
                          if (uCounts) {
                            if (uAppointments > 0) segments.push('#a855f7');
                            if (uTasks > 0) segments.push('#f59e0b');
                            if (uVideocalls > 0) segments.push('#22c55e');
                            if (uWorkgroupEvents > 0) segments.push('#8b5cf6');
                          } else if (count > 0) {
                            segments.push('#a855f7');
                          }
                          if (shiftPeopleCount > 0) segments.push(shiftColor);
                          return (
                            <>
                              {intensity > 0 && (
                                <span
                                  className="absolute inset-x-0 bottom-1 mx-auto w-1.5 h-1.5 rounded-full"
                                  style={{
                                    backgroundColor: '#a855f7',
                                    opacity: isSelected ? 1 : intensity,
                                  }}
                                  aria-hidden="true"
                                />
                              )}
                              {segments.length > 0 && (
                                <div className="absolute inset-x-1 bottom-0 h-0.5 rounded-full overflow-hidden flex">
                                  {segments.slice(0, 3).map((c, i) => (
                                    <span key={i} className="flex-1" style={{ backgroundColor: c }} />
                                  ))}
                                </div>
                              )}
                              {shiftPeopleCount > 0 && (
                                <span
                                  className="absolute top-0 right-0 flex items-center gap-px px-0.5 rounded-sm text-[9px] sm:text-[10px] font-bold leading-none"
                                  style={{ backgroundColor: `${shiftColor}30`, color: shiftColor }}
                                  title={shiftGroups.map(s => `${s.shift_name} ${s.start_time?.slice(0,5)}-${s.end_time?.slice(0,5)} @ ${s.store_name}: ${s.people.map(p => `${p.first_name} ${p.last_name}`).join(', ')}`).join('\n')}
                                >
                                  <Users className="w-2 h-2" />
                                  {shiftPeopleCount}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </button>
                    );
                  })}
                </div>

                {serviceTypes.length > 0 && (
                  <div className="mt-5 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Tipi</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {serviceTypes.filter(t => t.id && t.is_active).map((type) => (
                        <div key={type.id} className="flex items-center gap-1.5">
                          <div 
                            className="w-2.5 h-2.5 rounded-sm" 
                            style={{ backgroundColor: type.color ?? undefined }}
                          />
                          <span className="text-[11px] text-gray-600 dark:text-gray-400">{type.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className={cn("pt-3", serviceTypes.length > 0 ? "mt-2" : "mt-5 border-t border-gray-200 dark:border-gray-700")}>
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Stati</p>
                  <div className="flex flex-wrap gap-x-1.5 gap-y-1.5">
                    {Object.entries(STATUS_LABELS).map(([status, label]) => {
                      const StatusIcon = STATUS_ICONS[status];
                      const colors = STATUS_COLORS[status];
                      return (
                        <span
                          key={status}
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border',
                            colors!.bg!, colors!.text!, colors!.border!
                          )}
                        >
                          {StatusIcon && <StatusIcon className="w-2.5 h-2.5" />}
                          {label}
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Sorgenti</p>
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sourcesFilter.appointments}
                        onChange={(e) => setSourcesFilter(prev => ({ ...prev, appointments: e.target.checked }))}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                      <span className="text-[11px] text-gray-600 dark:text-gray-400">Appuntamenti</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sourcesFilter.tasks}
                        onChange={(e) => setSourcesFilter(prev => ({ ...prev, tasks: e.target.checked }))}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                      <span className="text-[11px] text-gray-600 dark:text-gray-400">Task</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sourcesFilter.videocalls}
                        onChange={(e) => setSourcesFilter(prev => ({ ...prev, videocalls: e.target.checked }))}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                      <span className="text-[11px] text-gray-600 dark:text-gray-400">Video Call</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sourcesFilter.workgroup_events}
                        onChange={(e) => setSourcesFilter(prev => ({ ...prev, workgroup_events: e.target.checked }))}
                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className="w-2.5 h-2.5 rounded-full bg-violet-500" />
                      <span className="text-[11px] text-gray-600 dark:text-gray-400">Eventi gruppo</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
            {isBroad && selectedUserId === 'all' && calendarUsers.length >= 2 && (
              <div className="mt-4 sm:mt-6">
                <CockpitAdminMatrix
                  users={calendarUsers}
                  events={timedEvents}
                  onCellClick={(userId, bandStartHour) => {
                    setSelectedUserId(userId);
                    const d = new Date(selectedDate);
                    d.setHours(bandStartHour, 0, 0, 0);
                    setSelectedDate(d);
                    handleTabChange('planning');
                  }}
                />
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-sm h-full flex flex-col">
              <CardHeader className="pb-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-900 dark:text-white flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-purple-600" />
                      {formatDate(selectedDate)}
                    </CardTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                      <span>{filteredUnifiedEvents.length} event{filteredUnifiedEvents.length === 1 ? 'o' : 'i'}</span>
                      {selectedUserId && selectedUserId !== 'all' && (() => {
                        const u = calendarUsers.find((x) => x.id === selectedUserId);
                        const label = u?.name || u?.email;
                        if (!label) return null;
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium border border-purple-200 dark:border-purple-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                            {label}
                          </span>
                        );
                      })()}
                      {googleAccountFilter !== 'all' && (
                        <span className="text-xs text-purple-500">({googleAccountFilter})</span>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      loadDayData();
                      loadUnifiedDayData();
                      loadDayShifts();
                      loadDayAttendance();
                      loadMonthData();
                      loadUnifiedMonthData();
                      loadMonthShifts();
                      toast({ title: 'Calendario aggiornato' });
                    }}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 flex-1 min-h-0 overflow-y-auto">
                {/* Task #560: Cockpit Apple-Style. Sostituisce l'intera griglia
                    con accordion (Mattina/Pomeriggio/Sera) con un cockpit
                    orientato al colpo d'occhio: card Adesso/Prossimo, mini
                    statistiche, lista eventi ridisegnata, matrice admin
                    utenti×fasce. Le interazioni di drag/drop e timeline restano
                    nel tab Planning. */}
                {(() => {
                  // Calcolo minuti occupati e orario lavorativo per la
                  // percentuale di occupazione visualizzata nelle stats.
                  const busyMinutes = filteredUnifiedEvents
                    .filter(e => !e.all_day && e.start_time_iso && e.end_time_iso)
                    .reduce((acc, e) => {
                      const s = new Date(e.start_time_iso!).getTime();
                      const en = new Date(e.end_time_iso!).getTime();
                      return acc + Math.max(0, Math.round((en - s) / 60000));
                    }, 0);
                  const parseHM = (s?: string): number | null => {
                    if (!s) return null;
                    const [h, m] = s.split(':').map(Number);
                    if (Number.isNaN(h) || Number.isNaN(m)) return null;
                    return h! * 60 + m!;
                  };
                  const wsm = parseHM(settings?.working_hours_start) ?? 9 * 60;
                  const wem = parseHM(settings?.working_hours_end) ?? 18 * 60;
                  const workingMinutes = Math.max(60, wem - wsm);
                  const counts = filteredUnifiedEvents.reduce(
                    (acc, e) => {
                      if (e.source === 'appointment') acc.appointments++;
                      else if (e.source === 'task') acc.tasks++;
                      else if (e.source === 'videocall') acc.videocalls++;
                      else if (e.source === 'workgroup_event') acc.wg++;
                      return acc;
                    },
                    { appointments: 0, tasks: 0, videocalls: 0, wg: 0 },
                  );
                  return (
                    <div className="p-4 space-y-4">
                      <CockpitNowNextCards
                        events={unifiedEvents}
                        selectedDate={selectedDate}
                        onEventClick={handleUnifiedEventClick}
                      />
                      <CockpitDailyStats
                        appointments={counts.appointments}
                        tasks={counts.tasks}
                        videocalls={counts.videocalls}
                        workgroupEvents={counts.wg}
                        busyMinutes={busyMinutes}
                        workingMinutes={workingMinutes}
                      />
                      <CockpitAllDayEvents
                        events={allDayEvents}
                        onEventClick={handleUnifiedEventClick}
                      />
                      <CockpitEventList
                        events={timedEvents}
                        statusColors={STATUS_COLORS}
                        statusIcons={STATUS_ICONS}
                        onEventClick={handleUnifiedEventClick}
                        onOpenInPlanning={(evt) => {
                          if (evt.assigned_to_id) setSelectedUserId(evt.assigned_to_id);
                          if (evt.start_time) {
                            const [hh, mm] = evt.start_time.split(':').map((n) => parseInt(n, 10));
                            if (!isNaN(hh!)) {
                              const d = new Date(selectedDate);
                              d.setHours(hh!, isNaN(mm!) ? 0 : mm, 0, 0);
                              setSelectedDate(d);
                            }
                          }
                          handleTabChange('planning');
                        }}
                      />
                    </div>
                  );
                })()}
                {isModuleEnabled('hr') && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShiftsExpanded(prev => !prev)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted/70 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-semibold text-foreground">
                          Personale in turno / Presenze
                        </span>
                        {(dayShifts.length > 0 || dayAttendance.length > 0) && (
                          <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700 text-[10px] px-1.5 py-0 border">
                            {dayShifts.reduce((acc, s) => acc + s.people.length, 0)}
                            {dayAttendance.length > 0 && ` / ${dayAttendance.filter(a => !a.clock_out).length} presenti`}
                          </Badge>
                        )}
                      </div>
                      <ChevronDown className={cn(
                        'w-4 h-4 text-gray-400 transition-transform duration-200',
                        shiftsExpanded && 'rotate-180'
                      )} />
                    </button>
                    {shiftsExpanded && (
                      <div className="p-4 bg-muted/50 space-y-3">
                        {dayShifts.length === 0 && dayAttendance.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            Nessun turno pianificato e nessuna timbratura per questa giornata
                          </p>
                        ) : (
                          <>
                            {dayShifts.length > 0 && (
                              <div className="space-y-3">
                                {dayShifts.map((shift) => (
                                  <div key={`${shift.shift_template_id}-${shift.store_name}`} className="rounded-lg border border-border bg-card p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <div
                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: shift.shift_color }}
                                      />
                                      <span className="text-sm font-medium text-foreground">
                                        {shift.shift_name} {shift.start_time?.slice(0, 5)}-{shift.end_time?.slice(0, 5)}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        @ {shift.store_name}{shift.store_city ? ` - ${shift.store_city}` : ''}
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {shift.people.map((person) => {
                                        const att = dayAttendance.find(a => a.user_id === person.user_id);
                                        const isActive = att && !att.clock_out;
                                        return (
                                          <div
                                            key={person.user_id}
                                            className={cn(
                                              "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs",
                                              isActive
                                                ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200"
                                                : att
                                                  ? "bg-gray-100 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400"
                                                  : "bg-muted text-foreground"
                                            )}
                                          >
                                            <div
                                              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white flex-shrink-0"
                                              style={{ backgroundColor: isActive ? '#22c55e' : att ? '#9ca3af' : shift.shift_color }}
                                            >
                                              {person.first_name?.[0]}{person.last_name?.[0]}
                                            </div>
                                            {person.first_name} {person.last_name}
                                            {att && (
                                              <span className="text-[10px] opacity-70 ml-1">
                                                {new Date(att.clock_in).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                                {att.clock_out ? `-${new Date(att.clock_out).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {(() => {
                              const shiftUserIds = new Set(dayShifts.flatMap(s => s.people.map(p => p.user_id)));
                              const extraAttendance = dayAttendance.filter(a => !shiftUserIds.has(a.user_id));
                              if (extraAttendance.length === 0) return null;
                              return (
                                <div className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3">
                                  <div className="flex items-center gap-2 mb-2">
                                    <AlertCircle className="w-3 h-3 text-amber-500" />
                                    <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                                      Presenti senza turno
                                    </span>
                                    <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700 text-[10px] px-1.5 py-0 border">
                                      {extraAttendance.length}
                                    </Badge>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {extraAttendance.map((att) => {
                                      const isActive = !att.clock_out;
                                      return (
                                        <div
                                          key={att.user_id}
                                          className={cn(
                                            "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs",
                                            isActive
                                              ? "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200"
                                              : "bg-gray-100 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400"
                                          )}
                                        >
                                          <div className={cn(
                                            "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white flex-shrink-0",
                                            isActive ? "bg-amber-500" : "bg-gray-400"
                                          )}>
                                            {att.first_name?.[0]}{att.last_name?.[0]}
                                          </div>
                                          {att.first_name} {att.last_name}
                                          <span className="text-[10px] opacity-70 ml-1">
                                            {new Date(att.clock_in).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                            {att.clock_out ? `-${new Date(att.clock_out).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}` : ''}
                                          </span>
                                          {att.store_name && (
                                            <span className="text-[10px] opacity-60">@ {att.store_name}</span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
          </TabsContent>
          <TabsContent value="planning" className="mt-0 px-3 md:px-6 pt-4">
            <AppointmentPlanningView
              access={access}
              canManage={canManage}
              selectedDate={selectedDate}
              settings={settings}
              calendarUsers={calendarUsers}
              selectedStoreId={selectedStoreId}
              selectedUserId={selectedUserId}
              serviceTypes={planningServiceTypes}
              sourceKinds={sourceKindsFilter}
              onDateChange={(d) => setSelectedDate(d)}
              onResourceClick={(uid, name) => {
                setDrawerResource({ id: uid, name });
                setDrawerOpen(true);
              }}
              onOpenCustomerCard={canViewCrm ? (appt) => {
                const name = appt.customer_name || 'Contatto';
                if (appt.person_id) {
                  setCustomerCardTarget({ type: 'person', id: appt.person_id, name });
                } else if (appt.customer_id) {
                  setCustomerCardTarget({ type: 'b2c', id: appt.customer_id, name });
                } else if (appt.lead_id) {
                  setCalendarLeadTimeline({ id: appt.lead_id, name });
                }
              } : undefined}
              onCellClick={(userId, slotStart, slotEnd, serviceId) => {
                if (!canManage) return;
                if (!canWriteCalendarObject(access, { assigned_to_id: userId }, 'create')) return;
                setPrefillCampaignId('');
                setNewAppointment({
                  title: '',
                  description: '',
                  service_type_id: serviceId || '',
                  start_time: toLocalDatetimeString(slotStart),
                  end_time: toLocalDatetimeString(slotEnd),
                  customer_name: '',
                  customer_email: '',
                  customer_phone: '',
                  customer_id: '',
                  lead_id: '',
                  assigned_to_id: userId,
                  observer_ids: [],
                  campaign_id: '',
                  status: 'scheduled',
                  location: '',
                  notes: '',
                  internal_notes: '',
                  store_id: selectedStoreId || '',
                  priority: 'normal',
                  reminder_offsets: [],
                  source_kind: 'manuale_admin',
                  appointment_form_id: '',
                  form_values: {},
                  product_id: '',
                  product_name: '',
                  product_price: null,
                  product_quantity: 1,
                  product_note: '',
                  scheduled_reminders: [],
                });
                setContactSearch('');
                setContactResults([]);
                setNewAppointmentStep(1);
                setShowNewAppointmentModal(true);
              }}
              onAppointmentClick={(id, appt) => {
                setPlanningDetailId(id);
                // Compose a typed AppointmentLite snapshot from the planning-view
                // payload so the detail panel has something to render before the
                // full record is fetched. No `any`/`unknown as` shortcut.
                const lite: AppointmentLite = {
                  id: appt.id,
                  user_id: appt.user_id,
                  person_id: appt.person_id,
                  customer_id: appt.customer_id,
                  lead_id: appt.lead_id,
                  title: appt.title,
                  start_time: appt.start_time,
                  end_time: appt.end_time,
                  status: appt.status,
                  customer_name: appt.customer_name ?? null,
                  assigned_to_id: appt.assigned_to_id ?? null,
                  type_name: appt.type_name ?? null,
                  type_color: appt.type_color ?? null,
                };
                setPlanningDetailInitial(lite);
                setPlanningDetailOpen(true);
              }}
            />
          </TabsContent>
          <TabsContent value="matrix" className="mt-0 px-3 md:px-6 pt-4">
            <BookingMatrixGrid
              canManage={canManage}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              settings={settings}
              calendarUsers={calendarUsers}
              selectedStoreId={selectedStoreId}
              selectedUserId={selectedUserId}
              serviceTypes={planningServiceTypes}
              onResourceClick={(uid, name) => {
                setDrawerResource({ id: uid, name });
                setDrawerOpen(true);
              }}
              onCellClick={(userId, slotStart, slotEnd, serviceId) => {
                if (!canManage || !canWriteCalendarObject(access, { assigned_to_id: userId }, 'create')) return;
                setPrefillCampaignId('');
                setNewAppointment({
                  title: '',
                  description: '',
                  service_type_id: serviceId || '',
                  start_time: toLocalDatetimeString(slotStart),
                  end_time: toLocalDatetimeString(slotEnd),
                  customer_name: '',
                  customer_email: '',
                  customer_phone: '',
                  customer_id: '',
                  lead_id: '',
                  assigned_to_id: userId,
                  observer_ids: [],
                  campaign_id: '',
                  status: 'scheduled',
                  location: '',
                  notes: '',
                  internal_notes: '',
                  store_id: selectedStoreId || '',
                  priority: 'normal',
                  reminder_offsets: [],
                  source_kind: 'manuale_admin',
                  appointment_form_id: '',
                  form_values: {},
                  product_id: '',
                  product_name: '',
                  product_price: null,
                  product_quantity: 1,
                  product_note: '',
                  scheduled_reminders: [],
                });
                setContactSearch('');
                setContactResults([]);
                setNewAppointmentStep(1);
                setShowNewAppointmentModal(true);
              }}
              onAppointmentClick={(id) => {
                const apt = dayAppointments.find(a => a.id === id);
                if (apt) {
                  setSelectedAppointment(apt);
                  setShowAppointmentModal(true);
                }
              }}
            />
          </TabsContent>
          <TabsContent value="skill-matrix" className="mt-0 px-3 md:px-6 pt-4">
            <SkillMatrixSettings />
          </TabsContent>
          <TabsContent value="analytics" className="mt-0 px-3 md:px-6 pt-4">
            <CalendarAnalyticsTab />
          </TabsContent>
          <TabsContent value="settings" className="mt-0 px-3 md:px-6 pt-4">
              {/* Pagina Impostazioni renderizzata embedded, senza header/Chiudi.
                  L'altezza si adatta al contenitore: lasciamo respiro al
                  resto della pagina (header app + topbar tab) usando viewport-px. */}
              <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden bg-white dark:bg-gray-900" style={{ minHeight: 'calc(100vh - 220px)' }}>
                <CalendarSettingsPage mode="embedded" access={access} />
              </div>
          </TabsContent>
          <TabsContent value="inbox" className="mt-0 px-3 md:px-6 pt-4">
            <CalendarInboxTab
              access={access}
              canManage={canManage}
              onCountChange={(delta) => setInboxCount(c => Math.max(0, c + delta))}
              onOpenAppointment={(id) => {
                const apt = dayAppointments.find(a => a.id === id) || appointments.find(a => a.id === id);
                if (apt) {
                  setSelectedAppointment(apt);
                  setShowAppointmentModal(true);
                } else {
                  apiClient.get<CalendarAppointment>(`/calendar/appointments/${id}`).then((res) => {
                    if (res?.success && res.data) {
                      setSelectedAppointment(res.data);
                      setShowAppointmentModal(true);
                    }
                  });
                }
              }}
              onOpenCustomerCard={canViewCrm ? (row) => {
                const name = row.customer_name || 'Contatto';
                if (row.person_id) {
                  setCustomerCardTarget({ type: 'person', id: row.person_id, name });
                } else if (row.customer_id) {
                  setCustomerCardTarget({ type: 'b2c', id: row.customer_id, name });
                } else if (row.lead_id) {
                  setCalendarLeadTimeline({ id: row.lead_id, name });
                }
              } : undefined}
            />
          </TabsContent>
        </Tabs>

      <Dialog open={showAppointmentModal} onOpenChange={setShowAppointmentModal}>
        <DialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900 dark:text-white">Dettagli Appuntamento</DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-12 rounded-full"
                  style={{ backgroundColor: selectedAppointment.type_color || '#6366f1' }}
                />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedAppointment.title}</h3>
                  {selectedAppointment.type_name && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{selectedAppointment.type_name}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Inizio</Label>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    {formatTime(selectedAppointment.start_time)}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Fine</Label>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <Clock className="w-4 h-4 text-purple-400" />
                    {formatTime(selectedAppointment.end_time)}
                  </div>
                </div>
              </div>

              {selectedAppointment.customer_name && (
                <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span>{selectedAppointment.customer_name}</span>
                  </div>
                  {selectedAppointment.customer_email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Mail className="w-4 h-4" />
                      <span>{selectedAppointment.customer_email}</span>
                    </div>
                  )}
                  {selectedAppointment.customer_phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Phone className="w-4 h-4" />
                      <span>{selectedAppointment.customer_phone}</span>
                    </div>
                  )}
                </div>
              )}

              {selectedAppointment.store_name && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Sede</Label>
                  <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                    {selectedAppointment.store_name}
                  </Badge>
                </div>
              )}

              {selectedAppointment.location && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  <MapPin className="w-4 h-4" />
                  <span>{selectedAppointment.location}</span>
                </div>
              )}

              {selectedAppointment.meeting_link && (
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-indigo-500" />
                  <a
                    href={selectedAppointment.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                  >
                    Avvia Video Meeting
                  </a>
                </div>
              )}

              {selectedAppointment.assigned_to_name && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Assegnato a</Label>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedAppointment.assigned_to_name}</span>
                  </div>
                </div>
              )}

              {selectedAppointment.campaign_name && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Campagna</Label>
                  <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400">
                    {selectedAppointment.campaign_name}
                  </Badge>
                </div>
              )}

              {/* Task #526/#546: Servizio (product/SKU acquistato) da metadata.product_name — sempre visibile, fallback "—" */}
              <div className="space-y-1">
                <Label className="text-gray-500 dark:text-gray-400 text-xs">Servizio</Label>
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {appointmentMetadata(selectedAppointment).product_name || '—'}
                  </span>
                  {appointmentMetadata(selectedAppointment).product_name && appointmentMetadata(selectedAppointment).product_price != null && (
                    <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 text-[10px]">
                      {String(appointmentMetadata(selectedAppointment).product_price)}
                      {appointmentMetadata(selectedAppointment).product_currency
                        ? ` ${appointmentMetadata(selectedAppointment).product_currency}`
                        : ' €'}
                    </Badge>
                  )}
                </div>
              </div>

              {(selectedAppointment.appointment_form_id || selectedAppointment.form_slug || appointmentMetadata(selectedAppointment).source === 'public_form') && (
                <div className="space-y-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300">
                      Prenotazione web
                    </Badge>
                    {selectedAppointment.form_name && (
                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        via <strong>{selectedAppointment.form_name}</strong>
                        {selectedAppointment.form_slug ? ` (/${selectedAppointment.form_slug})` : ''}
                      </span>
                    )}
                  </div>
                  {canEditAppointment(selectedAppointment) && selectedAppointment.status === 'scheduled' && (
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleUpdateAppointmentStatus(selectedAppointment.id, 'confirmed')}
                      >
                        <Check className="w-4 h-4 mr-1" /> Approva
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        onClick={() => handleUpdateAppointmentStatus(selectedAppointment.id, 'cancelled')}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Rifiuta
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {selectedAppointment.description && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Descrizione</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedAppointment.description}</p>
                </div>
              )}

              {selectedAppointment.notes && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Note</Label>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{selectedAppointment.notes}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-gray-500 dark:text-gray-400 text-xs">Stato</Label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_LABELS).map(([status, label]) => (
                    <Button
                      key={status}
                      disabled={!canEditAppointment(selectedAppointment)}
                      variant={selectedAppointment.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleUpdateAppointmentStatus(selectedAppointment.id, status)}
                      className={cn(
                        selectedAppointment.status === status
                          ? 'bg-purple-600 text-white'
                          : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                      )}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                {canEditAppointment(selectedAppointment) && googleSyncStatus?.enabled && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20"
                    onClick={() => handleSyncSingleGoogle(selectedAppointment.id)}
                    disabled={syncingGoogle}
                  >
                    {syncingGoogle ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-1" />
                    )}
                    Google Sync
                  </Button>
                )}
                {canViewCrm && (selectedAppointment.person_id || selectedAppointment.customer_id || selectedAppointment.lead_id) && (
                  <Button
                    variant="outline"
                    className="border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    onClick={() => {
                      // Preferenza person_id (vista aggregata cross-record);
                      // fallback a customer (b2c) o lead se person_id mancante.
                      const pid = selectedAppointment.person_id;
                      if (pid) {
                        setCustomerCardTarget({
                          type: 'person',
                          id: pid,
                          name: selectedAppointment.customer_name || selectedAppointment.title || 'Persona',
                        });
                      } else if (selectedAppointment.customer_id) {
                        setCustomerCardTarget({
                          type: 'b2c',
                          id: selectedAppointment.customer_id,
                          name: selectedAppointment.customer_name || 'Cliente',
                        });
                      } else if (selectedAppointment.lead_id) {
                        setCalendarLeadTimeline({
                          id: selectedAppointment.lead_id,
                          name: selectedAppointment.customer_name || selectedAppointment.title || 'Lead',
                        });
                      }
                    }}
                    data-testid="btn-open-customer-card"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Scheda cliente
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                  onClick={() => handleOpenEditAppointment(selectedAppointment)}
                  disabled={!canEditAppointment(selectedAppointment)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Modifica
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteAppointment(selectedAppointment.id)}
                  disabled={!canDeleteAppointment(selectedAppointment)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scheda cliente aggregata per persona (apre da Dettagli appuntamento). */}
      <InteractionHistoryModal
        open={!!customerCardTarget}
        onOpenChange={(o) => { if (!o) setCustomerCardTarget(null); }}
        target={customerCardTarget}
      />
      {/* Timeline lead per appuntamenti senza customer collegato. */}
      {calendarLeadTimeline && (
        <LeadTimelineModal
          open={!!calendarLeadTimeline}
          onOpenChange={(o) => { if (!o) setCalendarLeadTimeline(null); }}
          leadId={calendarLeadTimeline.id}
          leadName={calendarLeadTimeline.name}
        />
      )}

      <Dialog open={canManage && showNewAppointmentModal} onOpenChange={(open) => { setShowNewAppointmentModal(open); if (!open) setNewAppointmentStep(1); }}>
          <DialogContent
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100 w-[95vw] sm:max-w-2xl h-[100dvh] sm:h-[90vh] sm:rounded-2xl flex flex-col sm:!grid-rows-none sm:!gap-0 sm:!p-0 shadow-2xl"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                if (savingAppointment) return;
                if (!newAppointment.service_type_id || !newAppointment.start_time || !newAppointment.end_time) return;
                e.preventDefault();
                handleCreateAppointment();
              }
            }}
          >
            {(() => {
              const startD = newAppointment.start_time ? new Date(newAppointment.start_time) : null;
              const endD = newAppointment.end_time ? new Date(newAppointment.end_time) : null;
              const validTimes = !!(startD && endD && !Number.isNaN(startD.getTime()) && !Number.isNaN(endD.getTime()) && endD > startD);
              const headerDate = startD && !Number.isNaN(startD.getTime()) ? startD : selectedDate;
              const dateLabel = headerDate.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
              const timeRange = validTimes
                ? ` · ${startD.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}–${endD.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
                : '';
              const durMin = validTimes ? Math.round((endD.getTime() - startD.getTime()) / 60000) : 0;
              const durLabel = durMin > 0 ? ` · ${durMin} min` : '';
              return (
                <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <DialogTitle className="text-xl font-semibold tracking-tight">Nuovo Appuntamento</DialogTitle>
                      <DialogDescription className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
                        {dateLabel}{timeRange}{durLabel}
                      </DialogDescription>
                    </div>
                    <div className="flex items-center gap-0.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-0.5 text-xs flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => { setNaMode('rapido'); setNaMoreOpen(false); }}
                        className={cn(
                          "px-3 py-1 rounded-full font-medium transition-all",
                          naMode === 'rapido'
                            ? "bg-purple-600 text-white shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                        )}
                        title="Mostra solo i campi essenziali"
                      >
                        Rapido
                      </button>
                      <button
                        type="button"
                        onClick={() => { setNaMode('completo'); setNaMoreOpen(true); }}
                        className={cn(
                          "px-3 py-1 rounded-full font-medium transition-all",
                          naMode === 'completo'
                            ? "bg-purple-600 text-white shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                        )}
                        title="Mostra tutti i campi"
                      >
                        Completo
                      </button>
                      <button
                        type="button"
                        onClick={() => { setNaMode('personale'); setNaMoreOpen(false); }}
                        className={cn(
                          "px-3 py-1 rounded-full font-medium transition-all",
                          naMode === 'personale'
                            ? "bg-purple-600 text-white shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                        )}
                        title="Evento personale privato (non condiviso)"
                      >
                        Personale
                      </button>
                    </div>
                  </div>
                </DialogHeader>
              );
            })()}

            <div className="space-y-5 overflow-y-auto overflow-x-hidden flex-1 min-h-0 px-6 py-5">
              {/* Task #3040 — corpo appuntamento (Rapido/Completo). Nascosto in Personale. */}
              {naMode !== 'personale' && (<>
              {/* 1. Titolo */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Titolo</Label>
                <Input
                  value={newAppointment.title}
                  onChange={(e) => {
                    setNaTitleDirty(true);
                    setNewAppointment({ ...newAppointment, title: e.target.value });
                  }}
                  placeholder="Auto: Tipo – Cliente"
                  className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 focus-visible:ring-purple-500/40 focus-visible:border-purple-500"
                />
                {!naTitleDirty && newAppointment.title && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">Generato automaticamente — modifica per personalizzarlo.</p>
                )}
              </div>

              {/* 2. Tipo Appuntamento */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Tipo appuntamento *</Label>
                <Select
                  value={newAppointment.service_type_id}
                  onValueChange={(value) => {
                    const type = serviceTypes.find(t => t.id === value);
                    const updates: typeof newAppointment = { ...newAppointment, service_type_id: value };
                    if (type && newAppointment.start_time) {
                      const start = new Date(newAppointment.start_time);
                      const end = new Date(start);
                      end.setMinutes(end.getMinutes() + type.duration);
                      updates.end_time = toLocalDatetimeString(end);
                    }
                    setNewAppointment(updates);
                    if (type) {
                      const dateStr = toLocalDateStr(selectedDate);
                      const userParam = selectedUserId && selectedUserId !== 'all' && isBroad ? `&user=${selectedUserId}` : '';
                      apiClient.get<CalendarTimeSlot[]>(`/calendar/slots/${dateStr}?type=${value}${userParam}`).then(res => {
                        if (res.success) setTimeSlots(res.data || []);
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.filter(t => t.id && t.is_active).map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color ?? undefined }} />
                          {type.name}
                          <span className="text-xs text-gray-500 ml-1">({type.duration} min)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 3. Priorità */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Priorità</Label>
                <Select
                  value={newAppointment.priority}
                  onValueChange={(value: 'low' | 'normal' | 'high' | 'urgent') =>
                    setNewAppointment({ ...newAppointment, priority: value })
                  }
                >
                  <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                    <SelectValue placeholder="Priorità" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-gray-400" />Bassa</div></SelectItem>
                    <SelectItem value="normal"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-blue-500" />Normale</div></SelectItem>
                    <SelectItem value="high"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-amber-500" />Alta</div></SelectItem>
                    <SelectItem value="urgent"><div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-red-500" />Urgente</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 4. Cliente */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Cliente</Label>
                {(newAppointment.customer_id || newAppointment.lead_id) ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/50">
                    <Badge variant="outline" className={cn(
                      "text-xs flex-shrink-0",
                      newAppointment.customer_id ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200" : "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200"
                    )}>
                      {newAppointment.customer_id ? 'Cliente' : 'Lead'}
                    </Badge>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex-1">{newAppointment.customer_name}</span>
                    {newAppointment.customer_email && (
                      <span className="hidden md:inline text-xs text-gray-500 dark:text-gray-400 truncate">{newAppointment.customer_email}</span>
                    )}
                    <button
                      type="button"
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 flex-shrink-0 transition-colors"
                      onClick={() => {
                        setNewAppointment(prev => ({
                          ...prev,
                          customer_id: '',
                          lead_id: '',
                          customer_name: '',
                          customer_email: '',
                          customer_phone: ''
                        }));
                        setContactSearch('');
                      }}
                      title="Rimuovi cliente"
                    >
                      Rimuovi
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      value={contactSearch}
                      onChange={(e) => {
                        setContactSearch(e.target.value);
                        setShowContactDropdown(true);
                        searchContacts(e.target.value);
                      }}
                      onFocus={() => setShowContactDropdown(true)}
                      onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
                      placeholder="Cerca per nome, cognome, azienda, email o telefono..."
                      className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 focus-visible:ring-purple-500/40 focus-visible:border-purple-500"
                    />
                    {searchingContacts && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                    )}
                    {showContactDropdown && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-64 overflow-auto">
                        {contactSearch.length < 2 ? (
                          <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">Digita almeno 2 caratteri per cercare clienti e lead...</div>
                        ) : searchingContacts ? (
                          <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">Ricerca in corso...</div>
                        ) : contactResults.length > 0 ? (
                          contactResults.map((contact) => {
                            const display = contact.company_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || contact.phone || '—';
                            return (
                              <button
                                key={`${contact.contact_type}-${contact.id}`}
                                type="button"
                                className="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/60 flex items-center gap-2 transition-colors"
                                onClick={() => handleContactSelect(contact)}
                              >
                                <Badge variant="outline" className={cn(
                                  "text-xs flex-shrink-0",
                                  contact.contact_type === 'customer' ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400" : "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                                )}>
                                  {contact.contact_type === 'customer' ? 'Cliente' : 'Lead'}
                                </Badge>
                                <span className="font-medium truncate">{display}</span>
                                {contact.email && <span className="text-gray-500 dark:text-gray-400 text-xs truncate hidden md:inline">{contact.email}</span>}
                                {contact.phone && <span className="text-gray-500 dark:text-gray-400 text-xs truncate ml-auto">{contact.phone}</span>}
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-3 py-3 space-y-2">
                            <div className="text-sm text-gray-600 dark:text-gray-400 inline-flex items-center gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                              Nessun risultato per "<span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[140px] inline-block align-bottom">{contactSearch}</span>". Crealo:
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={!hasPermission('crm.manage')}
                                className="h-8 text-xs border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 flex-1"
                                onMouseDown={(e) => { e.preventDefault(); handleCreateFastCustomer('b2c'); setShowContactDropdown(false); }}
                              >
                                <UserPlus className="w-3.5 h-3.5 mr-1" /> Crea B2C
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={!hasPermission('crm.manage')}
                                className="h-8 text-xs border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 flex-1"
                                onMouseDown={(e) => { e.preventDefault(); handleCreateFastCustomer('b2b'); setShowContactDropdown(false); }}
                              >
                                <Building2 className="w-3.5 h-3.5 mr-1" /> Crea B2B
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 5. Inizio / Fine */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Inizio *</Label>
                  <Input
                    type="datetime-local"
                    value={newAppointment.start_time}
                    min={toLocalDatetimeString(new Date())}
                    onChange={(e) => {
                      const newStartTime = e.target.value;
                      const updates: typeof newAppointment = { ...newAppointment, start_time: newStartTime };
                      if (newStartTime && newAppointment.service_type_id) {
                        const type = serviceTypes.find(t => t.id === newAppointment.service_type_id);
                        if (type) {
                          const start = new Date(newStartTime);
                          const end = new Date(start);
                          end.setMinutes(end.getMinutes() + type.duration);
                          updates.end_time = toLocalDatetimeString(end);
                        }
                      }
                      setNewAppointment(updates);
                    }}
                    className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Fine *</Label>
                  <Input
                    type="datetime-local"
                    value={newAppointment.end_time}
                    min={newAppointment.start_time || toLocalDatetimeString(new Date())}
                    onChange={(e) => setNewAppointment({ ...newAppointment, end_time: e.target.value })}
                    className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                  />
                </div>
              </div>

              {/* Slot conflict warning */}
              {slotConflict && (
                <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0 text-xs text-amber-800 dark:text-amber-200">
                    <div className="font-medium">Slot già occupato da {slotConflict.occupiedBy}</div>
                    {slotConflict.nextFree && (
                      <button
                        type="button"
                        onClick={() => {
                          setNewAppointment(prev => ({
                            ...prev,
                            start_time: toLocalDatetimeString(new Date(slotConflict.nextFree!.start!)),
                            end_time: toLocalDatetimeString(new Date(slotConflict.nextFree!.end!)),
                          }));
                        }}
                        className="mt-1 inline-flex items-center gap-1 text-amber-900 dark:text-amber-100 underline underline-offset-2 hover:text-amber-700"
                      >
                        Sposta al prossimo libero: {new Date(slotConflict.nextFree.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* 6. Descrizione */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Descrizione</Label>
                <Textarea
                  value={newAppointment.description}
                  onChange={(e) => setNewAppointment({ ...newAppointment, description: e.target.value })}
                  placeholder="Breve descrizione visibile al cliente..."
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 resize-none focus-visible:ring-purple-500/40 focus-visible:border-purple-500"
                  rows={2}
                />
              </div>

              {/* 7. Note interne */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Note interne</Label>
                <Textarea
                  value={newAppointment.internal_notes}
                  onChange={(e) => setNewAppointment({ ...newAppointment, internal_notes: e.target.value })}
                  placeholder="Note visibili solo al team..."
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 resize-none focus-visible:ring-purple-500/40 focus-visible:border-purple-500"
                  rows={2}
                />
              </div>

              {/* 8. Sede */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Sede *</Label>
                <Select
                  value={newAppointment.store_id || '_none'}
                  onValueChange={(value) => {
                    const storeId = value === '_none' ? '' : value;
                    const store = stores.find(s => s.id === storeId);
                    const autoLocation = store ? [store.name, store.address, store.city].filter(Boolean).join(', ') : '';
                    setNewAppointment({ ...newAppointment, store_id: storeId, location: autoLocation });
                  }}
                >
                  <SelectTrigger className={`h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 ${!newAppointment.store_id ? 'border-red-400 dark:border-red-500' : ''}`}>
                    <SelectValue placeholder="Seleziona sede (obbligatoria)" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.filter(s => s.id).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.city ? ` - ${s.city}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {newAppointment.store_id && newAppointment.location && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {newAppointment.location}
                  </p>
                )}
              </div>

              {/* 9. Assegnato A */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Assegnato a</Label>
                <Select
                  value={newAppointment.assigned_to_id || user?.id}
                  onValueChange={(value) => {
                    setNaAssigneeDirty(true);
                    setNewAppointment({ ...newAppointment, assigned_to_id: value });
                  }}
                >
                  <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                    <SelectValue placeholder="Seleziona operatore" />
                  </SelectTrigger>
                  <SelectContent>
                    {naAssignableUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {u.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Best-resource suggestion (sempre visibile se applicabile) */}
              {newAppointment.service_type_id && newAppointment.start_time && newAppointment.end_time && (
                <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 px-3 py-2.5">
                  {bestResourceLoading ? (
                    <div className="flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Ricerca della risorsa migliore per lo slot···</span>
                    </div>
                  ) : bestResourceSuggestion ? (
                    <button
                      type="button"
                      onClick={() => {
                        setNaAssigneeDirty(true);
                        setNewAppointment({ ...newAppointment, assigned_to_id: bestResourceSuggestion.user_id });
                      }}
                      className="w-full text-left flex items-start gap-2 group"
                      title={bestResourceSuggestion.reasons.length > 0
                        ? `Score ${bestResourceSuggestion.score} · ${bestResourceSuggestion.reasons.join(' · ')}`
                        : `Score ${bestResourceSuggestion.score}`}
                    >
                      <Sparkles className="w-4 h-4 text-indigo-600 dark:text-indigo-300 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
                          Risorsa suggerita per questo slot
                        </div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:underline">
                          {bestResourceSuggestion.full_name}
                          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                            score {bestResourceSuggestion.score}
                          </span>
                        </div>
                        {bestResourceSuggestion.reasons.length > 0 && (
                          <div className="text-[11px] text-gray-600 dark:text-gray-400 truncate">
                            {bestResourceSuggestion.reasons.slice(0, 3).join(' · ')}
                          </div>
                        )}
                      </div>
                    </button>
                  ) : bestResourceNoCandidates ? (
                    <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>Nessuna risorsa idonea trovata per questo slot.</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* ============== SEZIONE COMPLETA ============== */}
              {naMode === 'completo' && (
                <>
                  <div className="relative pt-2">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white dark:bg-gray-900 px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Opzioni avanzate
                      </span>
                    </div>
                  </div>

                  {/* Servizio collegato al tipo — logica form pubblico (mode 'auto'):
                      con un solo prodotto si auto-seleziona e il selettore resta nascosto. */}
                  {newAppointment.service_type_id && shouldShowProductPicker('auto', linkedProducts) && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Servizio / Prodotto</Label>
                      <Select
                        value={newAppointment.product_id || '__none__'}
                        onValueChange={(value) => {
                          if (value === '__none__') {
                            setNewAppointment({
                              ...newAppointment,
                              product_id: '',
                              product_name: '',
                              product_price: null,
                            });
                            return;
                          }
                          const p = linkedProducts.find((x) => x.id === value);
                          setNewAppointment({
                            ...newAppointment,
                            product_id: value,
                            product_name: p?.name || '',
                            product_price: p?.price ?? null,
                          });
                        }}
                      >
                        <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                          <SelectValue placeholder="Nessun servizio" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Nessun servizio</SelectItem>
                          {linkedProducts.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}{p.price != null ? ` — € ${Number(p.price).toFixed(2)}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {newAppointment.product_id && (
                        <div className="grid grid-cols-2 gap-2 pt-1">
                          <div className="space-y-1">
                            <Label className="text-xs">Quantità</Label>
                            <Input
                              type="number"
                              min={1}
                              value={newAppointment.product_quantity}
                              onChange={(e) =>
                                setNewAppointment({
                                  ...newAppointment,
                                  product_quantity: Math.max(1, parseInt(e.target.value) || 1),
                                })
                              }
                              className="h-9 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Note</Label>
                            <Input
                              value={newAppointment.product_note}
                              onChange={(e) =>
                                setNewAppointment({ ...newAppointment, product_note: e.target.value })
                              }
                              placeholder="Es. taglia M, colore..."
                              className="h-9 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Origine */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Origine</Label>
                    <Select
                      value={newAppointment.source_kind}
                      onValueChange={(value) => {
                        const next = value as AppointmentSourceKind;
                        const clearForm = next !== 'form_pubblico';
                        const clearCampaign = next !== 'lead_campagna' && !prefillCampaignId;
                        setNewAppointment({
                          ...newAppointment,
                          source_kind: next,
                          appointment_form_id: clearForm ? '' : newAppointment.appointment_form_id,
                          form_values: clearForm ? {} : newAppointment.form_values,
                          campaign_id: clearCampaign ? '' : newAppointment.campaign_id,
                        });
                      }}
                    >
                      <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                        <SelectValue placeholder="Origine" />
                      </SelectTrigger>
                      <SelectContent>
                        {APPOINTMENT_SOURCE_TAXONOMY.map((s) => (
                          <SelectItem key={s.kind} value={s.kind}>
                            <div className="flex items-center gap-2">
                              <div className={cn('w-2.5 h-2.5 rounded-full border', s.bgClass, s.borderClass)} />
                              {s.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Determina come viene tracciata la sorgente in dashboard e statistiche.
                    </p>
                  </div>

                  {/* Sub-select dinamico in base all'origine */}
                  {newAppointment.source_kind === 'form_pubblico' && appointmentForms.length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Form di prenotazione</Label>
                      <Select
                        value={newAppointment.appointment_form_id || '__none__'}
                        onValueChange={(value) => {
                          const fid = value === '__none__' ? '' : value;
                          setNewAppointment({
                            ...newAppointment,
                            appointment_form_id: fid,
                            form_values: {},
                            source_kind: fid ? 'form_pubblico' : newAppointment.source_kind,
                          });
                        }}
                      >
                        <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                          <SelectValue placeholder="Nessun form" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Nessun form</SelectItem>
                          {appointmentForms.map((f) => (
                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(() => {
                        const form = appointmentForms.find((f) => f.id === newAppointment.appointment_form_id);
                        if (!form || !Array.isArray(form.custom_fields) || form.custom_fields.length === 0) return null;
                        return (
                          <div className="mt-2 space-y-2 p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Campi modulo
                            </p>
                            {form.custom_fields.map((cf) => {
                              const key = cf.id;
                              const val = newAppointment.form_values?.[key];
                              const setVal = (next: string | boolean) =>
                                setNewAppointment({
                                  ...newAppointment,
                                  form_values: { ...(newAppointment.form_values || {}), [key]: next },
                                });
                              return (
                                <div key={key} className="space-y-1">
                                  <Label className="text-xs">
                                    {cf.label}{cf.required ? ' *' : ''}
                                  </Label>
                                  {cf.type === 'textarea' ? (
                                    <Textarea
                                      value={typeof val === 'string' ? val : ''}
                                      onChange={(e) => setVal(e.target.value)}
                                      placeholder={cf.placeholder}
                                      rows={2}
                                      className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 resize-none text-sm"
                                    />
                                  ) : cf.type === 'select' ? (
                                    <Select
                                      value={typeof val === 'string' ? val : ''}
                                      onValueChange={(v) => setVal(v)}
                                    >
                                      <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                                        <SelectValue placeholder={cf.placeholder || 'Seleziona...'} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {(cf.options || []).map((opt) => (
                                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : cf.type === 'checkbox' ? (
                                    <div className="flex items-center gap-2 h-9">
                                      <input
                                        type="checkbox"
                                        checked={val === true}
                                        onChange={(e) => setVal(e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {cf.placeholder || cf.label}
                                      </span>
                                    </div>
                                  ) : cf.type === 'date' ? (
                                    <Input
                                      type="date"
                                      value={typeof val === 'string' ? val : ''}
                                      onChange={(e) => setVal(e.target.value)}
                                      placeholder={cf.placeholder}
                                      className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-sm"
                                    />
                                  ) : cf.type === 'number' ? (
                                    <Input
                                      type="number"
                                      value={typeof val === 'string' ? val : ''}
                                      onChange={(e) => setVal(e.target.value)}
                                      placeholder={cf.placeholder}
                                      inputMode="numeric"
                                      className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-sm"
                                    />
                                  ) : (
                                    <Input
                                      type={cf.type === 'email' ? 'email' : cf.type === 'phone' ? 'tel' : 'text'}
                                      value={typeof val === 'string' ? val : ''}
                                      onChange={(e) => setVal(e.target.value)}
                                      placeholder={cf.placeholder}
                                      className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-sm"
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {(newAppointment.source_kind === 'lead_campagna' || prefillCampaignId) && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Campagna</Label>
                      <Select
                        value={newAppointment.campaign_id || '__none__'}
                        onValueChange={(value) => setNewAppointment({ ...newAppointment, campaign_id: value === '__none__' ? '' : value })}
                        disabled={!!prefillCampaignId}
                      >
                        <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                          <SelectValue placeholder="Nessuna campagna" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Nessuna campagna</SelectItem>
                          {campaigns.filter(c => c.id).map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {prefillCampaignId && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">Ereditato dalla vista campagna</p>
                      )}
                    </div>
                  )}

                  {/* Video meeting */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Video call</Label>
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
                      <input
                        type="checkbox"
                        id="includeVideoMeeting"
                        checked={includeVideoMeeting}
                        onChange={(e) => setIncludeVideoMeeting(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="includeVideoMeeting" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none inline-flex items-center gap-1.5">
                        <Video className="w-4 h-4 text-indigo-500" /> Allega videocall
                      </label>
                    </div>
                  </div>

                  {/* Osservatori */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Osservatori</Label>
                    <Select
                      value={newAppointment.observer_ids?.length > 0 ? newAppointment.observer_ids[0] : undefined}
                      onValueChange={(value) => {
                        const observers = newAppointment.observer_ids || [];
                        if (observers.includes(value)) {
                          setNewAppointment({
                            ...newAppointment,
                            observer_ids: observers.filter(id => id !== value)
                          });
                        } else {
                          setNewAppointment({
                            ...newAppointment,
                            observer_ids: [...observers, value]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                        <SelectValue placeholder={
                          newAppointment.observer_ids?.length > 0
                            ? `${newAppointment.observer_ids.length} osservatori`
                            : "Nessun osservatore"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {calendarUsers.filter(u => u.id && u.id !== newAppointment.assigned_to_id).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            <div className="flex items-center gap-2">
                              {newAppointment.observer_ids?.includes(u.id) ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Users className="w-4 h-4 text-gray-400" />
                              )}
                              {u.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newAppointment.observer_ids && newAppointment.observer_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {newAppointment.observer_ids.map(id => {
                          const observerUser = calendarUsers.find(u => u.id === id);
                          return observerUser ? (
                            <Badge
                              key={id}
                              variant="secondary"
                              className="text-xs cursor-pointer hover:bg-red-100"
                              onClick={() => setNewAppointment({
                                ...newAppointment,
                                observer_ids: newAppointment.observer_ids.filter(oid => oid !== id)
                              })}
                            >
                              {observerUser.name} ×
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>

                  {/* Filtri rosa assegnatari — competenze e turni (mixabili) */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                      Filtra assegnatari
                      {naEligibilityLoading && (naFilterSkills || naFilterShifts) && (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                      )}
                    </Label>
                    <div className="px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          aria-pressed={naFilterSkills}
                          onClick={() => setNaFilterSkills((v) => !v)}
                          className={cn(
                            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                            naFilterSkills
                              ? 'bg-purple-600 border-purple-600 text-white'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400',
                          )}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Filtra per competenze
                        </button>
                        <button
                          type="button"
                          aria-pressed={naFilterShifts}
                          onClick={() => setNaFilterShifts((v) => !v)}
                          className={cn(
                            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                            naFilterShifts
                              ? 'bg-purple-600 border-purple-600 text-white'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-purple-400',
                          )}
                        >
                          <Clock className="w-3.5 h-3.5" />
                          Filtra per turni
                        </button>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {(naFilterSkills || naFilterShifts)
                          ? (!newAppointment.service_type_id || !newAppointment.start_time || !newAppointment.end_time)
                            ? 'Seleziona tipologia, inizio e fine per applicare i filtri.'
                            : `${naAssignableUsers.length} operatori idonei nella rosa "Assegnato a".`
                          : 'Restringi la rosa "Assegnato a" agli operatori con le competenze richieste e/o in turno sullo slot.'}
                      </p>
                    </div>
                  </div>

                  {/* Stato */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Stato</Label>
                    <Select
                      value={newAppointment.status}
                      onValueChange={(value: AppointmentStatus) =>
                        setNewAppointment({ ...newAppointment, status: value })
                      }
                    >
                      <SelectTrigger className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700">
                        <SelectValue placeholder="Seleziona stato" />
                      </SelectTrigger>
                      <SelectContent>
                        {APPOINTMENT_STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                              {s.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* AssigneeSkillsBadge inline in modalità Rapido per restare informativi */}
              {naMode === 'rapido' && newAppointment.service_type_id && (newAppointment.assigned_to_id || user?.id) && (
                <div className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30">
                  <AssigneeSkillsBadge
                    serviceId={newAppointment.service_type_id}
                    assigneeId={newAppointment.assigned_to_id || user?.id}
                    startTime={newAppointment.start_time}
                    endTime={newAppointment.end_time}
                  />
                </div>
              )}

              </>)}

              {/* ============== MODALITÀ PERSONALE ============== */}
              {naMode === 'personale' && (
                <>
                  <div className="rounded-lg border border-sky-200 dark:border-sky-900/50 bg-sky-50/60 dark:bg-sky-900/20 px-3 py-2 text-[11px] text-sky-700 dark:text-sky-300">
                    Evento privato: visibile solo a te, senza cliente, sede o promemoria. Occupa comunque la tua disponibilità.
                  </div>

                  {/* Titolo */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Titolo *</Label>
                    <Input
                      value={naPersonal.title}
                      onChange={(e) => setNaPersonal({ ...naPersonal, title: e.target.value })}
                      placeholder="Es. Ferie, Visita medica, Formazione…"
                      className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 focus-visible:ring-purple-500/40 focus-visible:border-purple-500"
                    />
                  </div>

                  {/* Tutto il giorno */}
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 px-3 py-2.5">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">Tutto il giorno</Label>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={naPersonal.all_day}
                      onClick={() => {
                        const next = !naPersonal.all_day;
                        const toDateOnly = (v: string): string => (v ? v.slice(0, 10) : '');
                        const toDateTime = (v: string): string => (v ? `${v.slice(0, 10)}T09:00` : '');
                        setNaPersonal({
                          ...naPersonal,
                          all_day: next,
                          start_time: next ? toDateOnly(naPersonal.start_time) : toDateTime(naPersonal.start_time),
                          end_time: next ? toDateOnly(naPersonal.end_time) : toDateTime(naPersonal.end_time),
                        });
                      }}
                      className={cn(
                        'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                        naPersonal.all_day ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600',
                      )}
                    >
                      <span
                        className={cn(
                          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                          naPersonal.all_day ? 'translate-x-6' : 'translate-x-1',
                        )}
                      />
                    </button>
                  </div>

                  {/* Inizio / Fine */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Inizio *</Label>
                      <Input
                        type={naPersonal.all_day ? 'date' : 'datetime-local'}
                        value={naPersonal.start_time}
                        onChange={(e) => setNaPersonal({ ...naPersonal, start_time: e.target.value })}
                        className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Fine *</Label>
                      <Input
                        type={naPersonal.all_day ? 'date' : 'datetime-local'}
                        value={naPersonal.end_time}
                        onChange={(e) => setNaPersonal({ ...naPersonal, end_time: e.target.value })}
                        className="h-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                      />
                    </div>
                  </div>

                  {/* Colore */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Colore</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      {['#0ea5e9', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#22c55e', '#ef4444', '#64748b'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setNaPersonal({ ...naPersonal, color: c })}
                          className={cn(
                            'w-7 h-7 rounded-full border-2 transition-transform',
                            naPersonal.color.toLowerCase() === c ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent hover:scale-105',
                          )}
                          style={{ backgroundColor: c }}
                          aria-label={`Colore ${c}`}
                        />
                      ))}
                      <input
                        type="color"
                        value={naPersonal.color}
                        onChange={(e) => setNaPersonal({ ...naPersonal, color: e.target.value })}
                        className="w-8 h-8 rounded cursor-pointer bg-transparent border border-gray-300 dark:border-gray-700"
                        aria-label="Colore personalizzato"
                      />
                    </div>
                  </div>

                  {/* Descrizione */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">Descrizione</Label>
                    <Textarea
                      value={naPersonal.description}
                      onChange={(e) => setNaPersonal({ ...naPersonal, description: e.target.value })}
                      placeholder="Note opzionali…"
                      rows={3}
                      className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 resize-none"
                    />
                  </div>
                </>
              )}

              <div className="pt-6 mt-2 border-t border-gray-100 dark:border-gray-800 flex justify-center items-center gap-10">
                <Button
                  variant="outline"
                  onClick={() => setShowNewAppointmentModal(false)}
                  className="border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 min-w-[140px]"
                >
                  Annulla
                </Button>
                <Button
                  onClick={naMode === 'personale' ? handleCreatePersonalEvent : handleCreateAppointment}
                  disabled={naMode === 'personale'
                    ? (savingPersonalEvent || !naPersonal.title.trim() || !naPersonal.start_time || !naPersonal.end_time)
                    : (savingAppointment || !newAppointment.store_id)}
                  className="bg-purple-600 hover:bg-purple-700 shadow-sm min-w-[200px]"
                >
                  {(savingAppointment || savingPersonalEvent) ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  {naMode === 'personale' ? 'Crea Evento' : 'Crea Appuntamento'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      <Dialog open={canManage && showEditAppointmentModal} onOpenChange={(open) => {
        setShowEditAppointmentModal(open);
        if (!open) setEditingAppointmentId(null);
      }}>
        <DialogContent 
          className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 max-w-xl h-[100dvh] sm:h-[85vh] sm:rounded-lg flex flex-col sm:flex sm:flex-col sm:!grid-rows-none sm:!gap-0 sm:!p-4"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-xl">Modifica Appuntamento</DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-gray-400">
              Modifica i dettagli dell'appuntamento
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0 px-1">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input
                value={newAppointment.title}
                onChange={(e) => setNewAppointment({ ...newAppointment, title: e.target.value })}
                placeholder="Es: Consulenza cliente"
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo appuntamento</Label>
              <Select
                value={newAppointment.service_type_id}
                onValueChange={(value) => {
                  setNewAppointment({ ...newAppointment, service_type_id: value });
                }}
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.filter(t => t.id && t.is_active).map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: type.color ?? undefined }} />
                        {type.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Campagna</Label>
              <Select
                value={newAppointment.campaign_id || '__none__'}
                onValueChange={(value) => setNewAppointment({ ...newAppointment, campaign_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                  <SelectValue placeholder="Nessuna campagna" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuna campagna</SelectItem>
                  {campaigns.filter(c => c.id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Stato</Label>
              <Select
                value={newAppointment.status}
                onValueChange={(value: AppointmentStatus) =>
                  setNewAppointment({ ...newAppointment, status: value })
                }
              >
                <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                  <SelectValue placeholder="Seleziona stato" />
                </SelectTrigger>
                <SelectContent>
                  {APPOINTMENT_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Inizio *</Label>
                <Input
                  type="datetime-local"
                  value={newAppointment.start_time}
                  onChange={(e) => setNewAppointment({ ...newAppointment, start_time: e.target.value })}
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
              <div className="space-y-2">
                <Label>Fine *</Label>
                <Input
                  type="datetime-local"
                  value={newAppointment.end_time}
                  min={newAppointment.start_time}
                  onChange={(e) => setNewAppointment({ ...newAppointment, end_time: e.target.value })}
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome cliente</Label>
                <Input
                  value={newAppointment.customer_name}
                  onChange={(e) => setNewAppointment({ ...newAppointment, customer_name: e.target.value })}
                  placeholder="Nome cliente"
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newAppointment.customer_email}
                  onChange={(e) => setNewAppointment({ ...newAppointment, customer_email: e.target.value })}
                  placeholder="email@esempio.it"
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telefono</Label>
                <Input
                  value={newAppointment.customer_phone}
                  onChange={(e) => setNewAppointment({ ...newAppointment, customer_phone: e.target.value })}
                  placeholder="+39..."
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
              <div className="space-y-2">
                <Label>Luogo</Label>
                <Input
                  value={newAppointment.location}
                  onChange={(e) => setNewAppointment({ ...newAppointment, location: e.target.value })}
                  placeholder="Es: Ufficio, Online..."
                  className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sede</Label>
                <Select
                  value={newAppointment.store_id || '_none'}
                  onValueChange={(value) => {
                    const storeId = value === '_none' ? '' : value;
                    const store = stores.find(s => s.id === storeId);
                    const autoLocation = store ? [store.name, store.address, store.city].filter(Boolean).join(', ') : '';
                    setNewAppointment({ ...newAppointment, store_id: storeId, location: autoLocation });
                  }}
                >
                  <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                    <SelectValue placeholder="Seleziona sede" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Nessuna sede</SelectItem>
                    {stores.filter(s => s.id).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}{s.city ? ` - ${s.city}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assegnato a</Label>
                <Select
                  value={newAppointment.assigned_to_id || user?.id}
                  onValueChange={(value) => setNewAppointment({ ...newAppointment, assigned_to_id: value })}
                >
                  <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                    <SelectValue placeholder="Seleziona operatore" />
                  </SelectTrigger>
                  <SelectContent>
                    {calendarUsers.filter(u => u.id).map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {u.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <AssigneeSkillsBadge
                  serviceId={newAppointment.service_type_id}
                  assigneeId={newAppointment.assigned_to_id || user?.id}
                  startTime={newAppointment.start_time}
                  endTime={newAppointment.end_time}
                  storeId={newAppointment.store_id}
                  users={calendarUsers.filter(u => u.id).map(u => ({ id: u.id, name: u.name }))}
                  onSelectAssignee={(id) => setNewAppointment({ ...newAppointment, assigned_to_id: id })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={newAppointment.notes}
                onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 resize-none"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => {
                setShowEditAppointmentModal(false);
                setEditingAppointmentId(null);
              }}
              className="border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleUpdateAppointment}
              disabled={savingAppointment}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingAppointment ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Aggiorna Appuntamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900 dark:text-white flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-amber-500" />
              Dettagli Task
            </DialogTitle>
          </DialogHeader>
          {selectedTaskEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-12 rounded-full bg-amber-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedTaskEvent.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTaskEvent.type_name}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Stato</Label>
                  <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
                    {selectedTaskEvent.status_label}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Priorità</Label>
                  {getPriorityBadge(selectedTaskEvent.metadata.priority) || <span className="text-sm text-gray-400">-</span>}
                </div>
              </div>
              {selectedTaskEvent.start_time && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Orario</Label>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <Clock className="w-4 h-4 text-amber-500" />
                    {selectedTaskEvent.start_time} - {selectedTaskEvent.end_time}
                  </div>
                </div>
              )}
              {selectedTaskEvent.all_day && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Scadenza</Label>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <CalendarIcon className="w-4 h-4 text-amber-500" />
                    {formatDate(selectedDate)}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowTaskModal(false)}
                  className="border-gray-300 dark:border-gray-600"
                >
                  Chiudi
                </Button>
                <Button
                  onClick={() => {
                    setShowTaskModal(false);
                    if (tasksPath) navigate(tasksPath);
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Apri Task
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900 dark:text-white flex items-center gap-2">
              <Video className="w-5 h-5 text-green-500" />
              Dettagli Video Call
            </DialogTitle>
          </DialogHeader>
          {selectedVideoEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-12 rounded-full bg-green-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedVideoEvent.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Video Call</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Stato</Label>
                  <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700">
                    {selectedVideoEvent.status_label}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Accesso</Label>
                  <div className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
                    {selectedVideoEvent.metadata.accessType === 'invite_only' ? (
                      <><Lock className="w-3.5 h-3.5" /> Solo invitati</>
                    ) : 'Aperto'}
                  </div>
                </div>
              </div>
              {selectedVideoEvent.start_time && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Orario</Label>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <Clock className="w-4 h-4 text-green-500" />
                    {selectedVideoEvent.start_time} - {selectedVideoEvent.end_time}
                  </div>
                </div>
              )}
              {selectedVideoEvent.metadata.creatorName && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Organizzatore</Label>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <User className="w-4 h-4 text-gray-500" />
                    {selectedVideoEvent.metadata.creatorName}
                  </div>
                </div>
              )}
              {selectedVideoEvent.metadata.workgroupName && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Gruppo di lavoro</Label>
                  <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <Users className="w-4 h-4 text-violet-500" />
                    {selectedVideoEvent.metadata.workgroupName}
                  </div>
                </div>
              )}
              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowVideoModal(false)}
                  className="border-gray-300 dark:border-gray-600"
                >
                  Chiudi
                </Button>
                {selectedVideoEvent.metadata.workgroupId && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      const groupId = selectedVideoEvent.metadata.workgroupId;
                      if (workgroupsPath && groupId) navigate(`${workgroupsPath}/${encodeURIComponent(groupId)}`);
                      setShowVideoModal(false);
                    }}
                    className="border-violet-300 dark:border-violet-600 text-violet-700 dark:text-violet-300"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Apri nel gruppo
                  </Button>
                )}
                {selectedVideoEvent.metadata.meetingLink && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedVideoEvent.metadata.meetingLink || '');
                        toast({ title: 'Link copiato', description: 'Link della video call copiato negli appunti' });
                      }}
                      className="border-gray-300 dark:border-gray-600"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copia link
                    </Button>
                    <Button
                      onClick={() => {
                        window.open(selectedVideoEvent.metadata.meetingLink, '_blank');
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Entra nella call
                    </Button>
                  </>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showWorkgroupEventModal} onOpenChange={setShowWorkgroupEventModal}>
        <DialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Evento gruppo
            </DialogTitle>
          </DialogHeader>
          {selectedWorkgroupEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-12 rounded-full bg-violet-500" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedWorkgroupEvent.title}</h3>
                  <p className="text-sm text-violet-500 dark:text-violet-400 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {selectedWorkgroupEvent.metadata.workgroupName}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Stato</Label>
                  <Badge className="bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-700">
                    {selectedWorkgroupEvent.status_label}
                  </Badge>
                </div>
                {selectedWorkgroupEvent.start_time && (
                  <div className="space-y-1">
                    <Label className="text-gray-500 dark:text-gray-400 text-xs">Orario</Label>
                    <div className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                      <Clock className="w-4 h-4 text-violet-500" />
                      {selectedWorkgroupEvent.start_time} - {selectedWorkgroupEvent.end_time}
                    </div>
                  </div>
                )}
              </div>
              {selectedWorkgroupEvent.metadata.description && (
                <div className="space-y-1">
                  <Label className="text-gray-500 dark:text-gray-400 text-xs">Descrizione</Label>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedWorkgroupEvent.metadata.description}</p>
                </div>
              )}
              <div className="bg-violet-50 dark:bg-violet-900/20 rounded-lg p-3 border border-violet-200 dark:border-violet-700">
                <p className="text-xs text-violet-600 dark:text-violet-400">
                  Questo evento è in sola lettura. Per modificarlo, apri il gruppo di lavoro.
                </p>
              </div>
              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowWorkgroupEventModal(false)}
                  className="border-gray-300 dark:border-gray-600"
                >
                  Chiudi
                </Button>
                {selectedWorkgroupEvent.metadata.workgroupId && (
                  <Button
                    onClick={() => {
                      const groupId = selectedWorkgroupEvent.metadata.workgroupId;
                      if (workgroupsPath && groupId) navigate(`${workgroupsPath}/${encodeURIComponent(groupId)}`);
                      setShowWorkgroupEventModal(false);
                    }}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Apri nel gruppo
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={canManage && skillOverrideDialog.open}
        onOpenChange={(open) => {
          if (!submittingOverride) {
            setSkillOverrideDialog((s) => ({ ...s, open }));
          }
        }}
      >
        <DialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Skill mancanti per la risorsa
            </DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-gray-400">
              {skillOverrideDialog.message ||
                'La risorsa selezionata non possiede tutte le competenze richieste dal servizio.'}
            </DialogDescription>
          </DialogHeader>

          {skillOverrideDialog.missingSkills.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3">
              <div className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
                Skill mancanti
              </div>
              <div className="flex flex-wrap gap-1">
                {skillOverrideDialog.missingSkills.map((s) => (
                  <Badge key={s} variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {skillOverrideDialog.canOverride ? (
            <div className="space-y-2">
              <Label htmlFor="skill-override-reason">Motivazione (obbligatoria)</Label>
              <Textarea
                id="skill-override-reason"
                rows={3}
                value={skillOverrideReason}
                onChange={(e) => setSkillOverrideReason(e.target.value)}
                placeholder="Indica perché vuoi assegnare comunque questa risorsa..."
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 resize-none"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                La motivazione verrà registrata nello storico dell'appuntamento.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Non hai i permessi per ignorare questo controllo. Seleziona una risorsa con le skill richieste o
              chiedi a un amministratore.
            </p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSkillOverrideDialog((s) => ({ ...s, open: false }))}
              disabled={submittingOverride}
            >
              Annulla
            </Button>
            {skillOverrideDialog.canOverride && (
              <Button
                onClick={confirmSkillOverride}
                disabled={submittingOverride || skillOverrideReason.trim().length < 3}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {submittingOverride ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Salva con override
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #526: Drawer dettagli risorsa (skill / turni / servizi) */}
      <PlanningSkillDrawer
        open={drawerOpen}
        onOpenChange={(o) => { setDrawerOpen(o); if (!o) setDrawerResource(null); }}
        userId={drawerResource?.id || null}
        userName={drawerResource?.name || null}
        canEdit={hasPermission('hr.settings.manage')}
      />

      {/* Task #545: pannello dettaglio appuntamento per il tab Planning */}
      <AppointmentDetailPanel
        access={access}
        canManage={canManage}
        open={planningDetailOpen}
        onOpenChange={(o) => {
          setPlanningDetailOpen(o);
          if (!o) {
            setPlanningDetailId(null);
            setPlanningDetailInitial(null);
          }
        }}
        appointmentId={planningDetailId}
        initial={planningDetailInitial}
        onChanged={() => loadDayData()}
        onEdit={(a) => {
          const apt = dayAppointments.find((x) => x.id === a.id) || appointments.find((x) => x.id === a.id);
          if (apt) {
            setSelectedAppointment(apt);
            setShowAppointmentModal(true);
            setPlanningDetailOpen(false);
          } else {
            apiClient.get<CalendarAppointment>(`/calendar/appointments/${a.id}`).then((res) => {
              if (res.success && res.data) {
                setSelectedAppointment(res.data);
                setShowAppointmentModal(true);
                setPlanningDetailOpen(false);
              }
            });
          }
        }}
        onReschedule={(a) => {
          const apt = dayAppointments.find((x) => x.id === a.id) || appointments.find((x) => x.id === a.id);
          if (apt) {
            setSelectedAppointment(apt);
            setShowAppointmentModal(true);
            setPlanningDetailOpen(false);
          } else {
            apiClient.get<CalendarAppointment>(`/calendar/appointments/${a.id}`).then((res) => {
              if (res.success && res.data) {
                setSelectedAppointment(res.data);
                setShowAppointmentModal(true);
                setPlanningDetailOpen(false);
              }
            });
          }
        }}
        onOpenCustomer={canViewCrm ? (a) => {
          const name = a.customer_name || 'Contatto';
          if (a.person_id) setCustomerCardTarget({ type: 'person', id: a.person_id, name });
          else if (a.customer_id) setCustomerCardTarget({ type: 'b2c', id: a.customer_id, name });
          else if (a.lead_id) setCalendarLeadTimeline({ id: a.lead_id, name });
        } : undefined}
      />

      {/* Mobile FAB — quick access to "Nuovo appuntamento" on phones.
          Hidden on tablet+ where the inline header button is reachable. */}
      {canManage && activeTab === 'calendar' && (
        <button
          type="button"
          onClick={() => {
            const now = new Date();
            const startTime = new Date(selectedDate);
            startTime.setHours(now.getHours() + 1, 0, 0, 0);
            const endTime = new Date(startTime);
            endTime.setMinutes(endTime.getMinutes() + (settings?.slot_duration || 30));
            setPrefillCampaignId('');
            setNewAppointment({
              title: '', description: '', service_type_id: '',
              start_time: toLocalDatetimeString(startTime),
              end_time: toLocalDatetimeString(endTime),
              customer_name: '', customer_email: '', customer_phone: '',
              customer_id: '', lead_id: '',
              assigned_to_id: user?.id || '',
              observer_ids: [], campaign_id: '', status: 'scheduled',
              location: '', notes: '', internal_notes: '',
              store_id: selectedStoreId || '',
              priority: 'normal', reminder_offsets: [],
              source_kind: 'manuale_admin',
              appointment_form_id: '', form_values: {},
              product_id: '', product_name: '', product_price: null,
              product_quantity: 1, product_note: '', scheduled_reminders: [],
            });
            setContactSearch('');
            setContactResults([]);
            setNewAppointmentStep(1);
            setShowNewAppointmentModal(true);
          }}
          className="md:hidden fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white shadow-lg shadow-purple-600/30 flex items-center justify-center transition-colors"
          aria-label="Nuovo appuntamento"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}
    </HubShell>
  );
}
